import {
    useQuery,
    useMutation,
    useQueryClient,
    queryOptions,
    UseQueryResult
} from '@tanstack/react-query';
import { ServiceBooking, Service, Business, Reservation, Person, User, Template, InventoryItem, ServiceGroup, InventoryStatus, ServiceOrder, ServicesPrices, ServiceProvider, TenantBusiness, Tenant, ServiceSellPrice, ServiceSellCost, ExchangeRate, DiscountCategory, ServiceCategory, SystemLog, Ticket, Payment, Activity, SavedReport, ClientBalanceGroup, IssuesData, TrackedFlight, DeletedReservation, Product, AfipVoucher, ServiceLinkedService, ReservationPaxGroup, ServiceBookingPaxGroup } from '@turizi/shared';
import { Subscription } from '@/types/subscription-models';
import { ClientHistory, BusinessBalance, MoneyTransaction } from '@/types/money-models';
import api, { ApiResponse } from '@/services/api-service';
import { notify } from '@/lib/notify';

// ===============================
// Query Key Factories
// ===============================

export const queryKeys = {
    // Auth
    auth: {
        all: ['auth'] as const,
        me: () => [...queryKeys.auth.all, 'me'] as const,
    },

    // Public endpoints
    public: {
        all: ['public'] as const,
        servicesPrices: (ownerSlug?: string, clientSlug?: string) => [...queryKeys.public.all, 'services-prices', ownerSlug, clientSlug] as const,
        services: (params?: any) => [...queryKeys.public.all, 'services', params] as const,
        service: (id?: string) => [...queryKeys.public.all, 'service', id] as const,
        reservation: (slug: string) => [...queryKeys.public.all, 'reservation', slug] as const,
        filters: (params?: any) => [...queryKeys.public.all, 'filters', params] as const,
        businessBySlug: (slug: string) => [...queryKeys.public.all, 'business-by-slug', slug] as const,
        tenantBusiness: (tenantBusinessId: string) => [...queryKeys.public.all, 'tenant-business', tenantBusinessId] as const,
        tenant: (tenantId: string) => [...queryKeys.public.all, 'tenant', tenantId] as const,
    },

    // Subscriptions
    subscriptions: {
        all: ['subscriptions'] as const,
        lists: () => [...queryKeys.subscriptions.all, 'list'] as const,
        list: (params?: any) => [...queryKeys.subscriptions.lists(), params] as const,
        details: () => [...queryKeys.subscriptions.all, 'detail'] as const,
        detail: (id: string | number) => [...queryKeys.subscriptions.details(), id] as const,
    },

    // Actions
    actions: {
        all: ['actions'] as const,
        execute: (action: string, payload?: any) => [...queryKeys.actions.all, action, payload] as const,
    },

    // Users
    users: {
        all: ['users'] as const,
        lists: () => [...queryKeys.users.all, 'list'] as const,
        list: (params?: any) => [...queryKeys.users.lists(), params] as const,
        details: () => [...queryKeys.users.all, 'detail'] as const,
        detail: (id: string | number) => [...queryKeys.users.details(), id] as const,
        emailHasUser: (email: string) => [...queryKeys.users.all, 'email-has-user', email] as const,
    },

    // Tenants
    tenants: {
        all: ['tenants'] as const,
        detail: (id: string | number) => [...queryKeys.tenants.all, 'detail', id] as const,
    },

    // Templates
    templates: {
        all: ['templates'] as const,
        lists: () => [...queryKeys.templates.all, 'list'] as const,
        list: (params?: any) => [...queryKeys.templates.lists(), params] as const,
        details: () => [...queryKeys.templates.all, 'detail'] as const,
        detail: (id: string | number) => [...queryKeys.templates.details(), id] as const,
    },

    // Saved Reports
    savedReports: {
        all: ['saved-reports'] as const,
        lists: () => [...queryKeys.savedReports.all, 'list'] as const,
        list: (params?: any) => [...queryKeys.savedReports.lists(), params] as const,
        details: () => [...queryKeys.savedReports.all, 'detail'] as const,
        detail: (id: string | number) => [...queryKeys.savedReports.details(), id] as const,
    },

    // Businesses
    businesses: {
        all: ['businesses'] as const,
        lists: () => [...queryKeys.businesses.all, 'list'] as const,
        list: (params?: any) => [...queryKeys.businesses.lists(), params] as const,
        details: () => [...queryKeys.businesses.all, 'detail'] as const,
        detail: (id: string | number) => [...queryKeys.businesses.details(), id] as const,
        home: () => [...queryKeys.businesses.all, 'home'] as const,
    },

    // Tenant Businesses
    tenantBusinesses: {
        all: ['tenant-businesses'] as const,
        lists: () => [...queryKeys.tenantBusinesses.all, 'list'] as const,
        list: (params?: any) => [...queryKeys.tenantBusinesses.lists(), params] as const,
        details: () => [...queryKeys.tenantBusinesses.all, 'detail'] as const,
        detail: (id: string | number) => [...queryKeys.businesses.details(), id] as const,
        emailLogs: (id: string | number) => [...queryKeys.tenantBusinesses.all, 'email-logs', id] as const,
        allEmailLogs: () => [...queryKeys.tenantBusinesses.all, 'email-logs', 'all'] as const,
    },


    // Reservations
    reservations: {
        all: ['reservations'] as const,
        lists: () => [...queryKeys.reservations.all, 'list'] as const,
        list: (params?: any) => [...queryKeys.reservations.lists(), params] as const,
        agenda: (params?: any) => [...queryKeys.reservations.all, 'agenda', params] as const,
        assignedCoordinator: (params?: any) => [...queryKeys.reservations.all, 'assigned-coordinator', params] as const,
        details: () => [...queryKeys.reservations.all, 'detail'] as const,
        detail: (id: string | number) => [...queryKeys.reservations.details(), id] as const,
        emailLogs: (id: string | number) => [...queryKeys.reservations.detail(id), 'email-logs'] as const,
        emailThreads: (id: string | number) => [...queryKeys.reservations.detail(id), 'email-threads'] as const,
        emailThreadDetails: (id: string | number, threadId: string) => [...queryKeys.reservations.detail(id), 'email-threads', threadId] as const,
        paxGroups: (id: string | number) => [...queryKeys.reservations.detail(id), 'pax-groups'] as const,
    },

    googleIntegration: {
        all: ['google-integration'] as const,
        status: () => [...queryKeys.googleIntegration.all, 'status'] as const,
        accounts: () => [...queryKeys.googleIntegration.all, 'accounts'] as const,
        search: (q: string) => [...queryKeys.googleIntegration.all, 'search', q] as const,
    },

    // Deleted Reservations
    deletedReservations: {
        all: ['deleted-reservations'] as const,
        lists: () => [...queryKeys.deletedReservations.all, 'list'] as const,
        list: (params?: any) => [...queryKeys.deletedReservations.lists(), params] as const,
        details: () => [...queryKeys.deletedReservations.all, 'detail'] as const,
        detail: (id: string | number) => [...queryKeys.deletedReservations.details(), id] as const,
    },

    // Services
    services: {
        all: ['services'] as const,
        lists: () => [...queryKeys.services.all, 'list'] as const,
        list: (params?: any) => [...queryKeys.services.lists(), params] as const,
        details: () => [...queryKeys.services.all, 'detail'] as const,
        detail: (id: string | number) => [...queryKeys.services.details(), String(id)] as const,
        byProvider: (businessId: string | number) => [...queryKeys.services.all, 'by-provider', businessId] as const,
        by3rdParty: (businessId: string | number) => [...queryKeys.services.all, '3rd-party', businessId] as const,
        bookings: () => [...queryKeys.services.all, 'bookings'] as const,
        bookingsList: (params?: any) => [...queryKeys.services.bookings(), params] as const,
        bookingsLimits: (params?: any) => [...queryKeys.services.all, 'bookings-limits', params] as const,
        availableLimits: (params?: any) => [...queryKeys.services.all, 'available-limits', params] as const,
        inventory: (serviceId: string | number) => [...queryKeys.services.all, 'inventory', String(serviceId)] as const,
        filterOptions: (params?: any) => [...queryKeys.services.all, 'filter-options', params] as const,
        locations: (category?: string) => [...queryKeys.services.all, 'locations', category] as const,
    },

    // Service Groups
    serviceGroups: {
        all: ['service-groups'] as const,
        lists: () => [...queryKeys.serviceGroups.all, 'list'] as const,
        list: (params?: any) => [...queryKeys.serviceGroups.lists(), params] as const,
        details: () => [...queryKeys.serviceGroups.all, 'detail'] as const,
        detail: (id: string | number) => [...queryKeys.serviceGroups.details(), id] as const,
    },

    // Products
    products: {
        all: ['products'] as const,
        lists: () => [...queryKeys.products.all, 'list'] as const,
        list: (params?: any) => [...queryKeys.products.lists(), params] as const,
        details: () => [...queryKeys.products.all, 'detail'] as const,
        detail: (id: string | number) => [...queryKeys.products.details(), id] as const,
    },

    // Service Providers
    serviceProviders: {
        all: ['service-providers'] as const,
        lists: () => [...queryKeys.serviceProviders.all, 'list'] as const,
        list: (params?: any) => [...queryKeys.serviceProviders.lists(), params] as const,
        details: () => [...queryKeys.serviceProviders.all, 'detail'] as const,
        detail: (id: string | number) => [...queryKeys.serviceProviders.details(), id] as const,
    },

    // Service Linked Services
    serviceLinkedServices: {
        all: ['service-linked-services'] as const,
        lists: () => [...queryKeys.serviceLinkedServices.all, 'list'] as const,
        list: (params?: any) => [...queryKeys.serviceLinkedServices.lists(), params] as const,
        details: () => [...queryKeys.serviceLinkedServices.all, 'detail'] as const,
        detail: (id: string | number) => [...queryKeys.serviceLinkedServices.details(), id] as const,
    },

    // Service Bookings
    serviceBookings: {
        all: ['service-bookings'] as const,
        lists: () => [...queryKeys.serviceBookings.all, 'list'] as const,
        list: (params?: any) => [...queryKeys.serviceBookings.lists(), params] as const,
        details: () => [...queryKeys.serviceBookings.all, 'detail'] as const,
        detail: (id: string | number) => [...queryKeys.serviceBookings.details(), id] as const,
        assigned: (params?: any) => [...queryKeys.serviceBookings.all, 'assignedReservations', params] as const,
        assignedServiceOrders: (params?: any) => [...queryKeys.serviceBookings.all, 'assignedServiceOrders', params] as const,
    },

    serviceBookingInventoryLinks: {
        all: ['service-booking-inventory-links'] as const,
        list: (params?: any) => [...queryKeys.serviceBookingInventoryLinks.all, params] as const,
    },

    // Service Orders
    serviceOrders: {
        all: ['service-orders'] as const,
        lists: () => [...queryKeys.serviceOrders.all, 'list'] as const,
        list: (params?: any) => [...queryKeys.serviceOrders.lists(), params] as const,
        details: () => [...queryKeys.serviceOrders.all, 'detail'] as const,
        detail: (id: string | number) => [...queryKeys.serviceOrders.details(), id] as const,
    },

    serviceSellPrices: {
        all: ['service-sell-prices'] as const,
        lists: () => [...queryKeys.serviceSellPrices.all, 'list'] as const,
        list: (params?: any) => [...queryKeys.serviceSellPrices.lists(), params] as const,
        details: () => [...queryKeys.serviceSellPrices.all, 'detail'] as const,
        detail: (id: string | number) => [...queryKeys.serviceSellPrices.details(), id] as const,
        active: () => [...queryKeys.serviceSellPrices.all, 'active'] as const,
    },

    serviceSellCosts: {
        all: ['service-sell-costs'] as const,
        lists: () => [...queryKeys.serviceSellCosts.all, 'list'] as const,
        list: (params?: any) => [...queryKeys.serviceSellCosts.lists(), params] as const,
        details: () => [...queryKeys.serviceSellCosts.all, 'detail'] as const,
        detail: (id: string | number) => [...queryKeys.serviceSellCosts.details(), id] as const,
        active: () => [...queryKeys.serviceSellCosts.all, 'active'] as const,
    },

    // Persons
    persons: {
        all: ['persons'] as const,
        lists: () => [...queryKeys.persons.all, 'list'] as const,
        list: (params?: any) => [...queryKeys.persons.lists(), params] as const,
        details: () => [...queryKeys.persons.all, 'detail'] as const,
        detail: (id: string | number) => [...queryKeys.persons.details(), id] as const,
        staff: () => [...queryKeys.persons.all, 'staff'] as const,
        staffList: () => [...queryKeys.persons.staff(), 'list'] as const,
    },

    // Inventory Items
    inventoryItems: {
        all: ['inventory-items'] as const,
        lists: () => [...queryKeys.inventoryItems.all, 'list'] as const,
        list: (params?: any) => [...queryKeys.inventoryItems.lists(), params] as const,
        details: () => [...queryKeys.inventoryItems.all, 'detail'] as const,
        detail: (id: string | number) => [...queryKeys.inventoryItems.details(), id] as const,
        statuses: () => [...queryKeys.inventoryItems.all, 'status'] as const,
        status: (params?: any) => [...queryKeys.inventoryItems.statuses(), params] as const,
    },



    // Reservation Payments
    reservationPayments: {
        all: ['reservation-payments'] as const,
        lists: () => [...queryKeys.reservationPayments.all, 'list'] as const,
        list: (params?: any) => [...queryKeys.reservationPayments.lists(), params] as const,
        details: () => [...queryKeys.reservationPayments.all, 'detail'] as const,
        detail: (id: string | number) => [...queryKeys.reservationPayments.details(), id] as const,
        byReservation: (reservationId: string | number) => [...queryKeys.reservationPayments.lists(), 'reservation', reservationId] as const,
    },

    // Service Categories
    serviceCategories: {
        all: ['service-categories'] as const,
        lists: () => [...queryKeys.serviceCategories.all, 'lists'] as const,
        list: (params?: any) => [...queryKeys.serviceCategories.lists(), params] as const,
        details: () => [...queryKeys.serviceCategories.all, 'detail'] as const,
    },

    moneyTransactions: {
        all: ['money-transactions'] as const,
        lists: () => [...queryKeys.moneyTransactions.all, 'list'] as const,
        list: (params?: any) => [...queryKeys.moneyTransactions.lists(), params] as const,
        listsIn: () => [...queryKeys.moneyTransactions.all, 'list', 'in'] as const,
        listIn: (params?: any) => [...queryKeys.moneyTransactions.listsIn(), params] as const,
        listsOut: () => [...queryKeys.moneyTransactions.all, 'list', 'out'] as const,
        listOut: (params?: any) => [...queryKeys.moneyTransactions.listsOut(), params] as const,
        details: () => [...queryKeys.moneyTransactions.all, 'detail'] as const,
        detail: (id: string | number) => [...queryKeys.moneyTransactions.details(), id] as const,
        byReservation: (reservationId: string | number) => [...queryKeys.moneyTransactions.lists(), 'reservation', reservationId] as const,
        byBooking: (bookingId: string | number) => [...queryKeys.moneyTransactions.lists(), 'booking', bookingId] as const,
    },

    businessBalance: {
        all: ['client-balance'] as const,
        lists: () => [...queryKeys.businessBalance.all, 'list'] as const,
        list: (params?: any) => [...queryKeys.businessBalance.lists(), params] as const,
    },

    clientHistory: {
        all: ['business-statement'] as const,
        lists: () => [...queryKeys.clientHistory.all, 'list'] as const,
        list: (params?: any) => [...queryKeys.clientHistory.lists(), params] as const,
    },

    personStatement: {
        all: ['person-statement'] as const,
        lists: () => [...queryKeys.personStatement.all, 'list'] as const,
        list: (params?: any) => [...queryKeys.personStatement.lists(), params] as const,
    },

    exchangeRates: {
        all: ['exchange-rates'] as const,
        lists: () => [...queryKeys.exchangeRates.all, 'list'] as const,
        list: (params?: any) => [...queryKeys.exchangeRates.lists(), params] as const,
        details: () => [...queryKeys.exchangeRates.all, 'detail'] as const,
        detail: (id: string | number) => [...queryKeys.exchangeRates.details(), id] as const,
    },

    inputSuggestions: {
        all: ['input-suggestions'] as const,
        get: (params: { type: string; id: string | number; key: string }) =>
            [...queryKeys.inputSuggestions.all, params.type, params.id, params.key] as const,
    },

    discountCategories: {
        all: ['discount-categories'] as const,
        lists: () => [...queryKeys.discountCategories.all, 'list'] as const,
        list: (params?: any) => [...queryKeys.discountCategories.lists(), params] as const,
        details: () => [...queryKeys.discountCategories.all, 'detail'] as const,
        detail: (id: string | number) => [...queryKeys.discountCategories.details(), id] as const,
    },

    systemLogs: {
        all: ['system-logs'] as const,
        lists: () => [...queryKeys.systemLogs.all, 'list'] as const,
        list: (params?: any) => [...queryKeys.systemLogs.lists(), params] as const,
    },

    activities: {
        all: ['activities'] as const,
        lists: () => [...queryKeys.activities.all, 'list'] as const,
        list: (params?: any) => [...queryKeys.activities.lists(), params] as const,
    },

    tickets: {
        all: ['tickets'] as const,
        lists: () => [...queryKeys.tickets.all, 'list'] as const,
        list: (params?: any) => [...queryKeys.tickets.lists(), params] as const,
        details: () => [...queryKeys.tickets.all, 'detail'] as const,
        detail: (id: string | number) => [...queryKeys.tickets.details(), id] as const,
    },

    homeFeed: {
        all: ['home-feed'] as const,
        get: () => [...queryKeys.homeFeed.all, 'get'] as const,
    },

    payments: {
        all: ['payments'] as const,
        lists: () => [...queryKeys.payments.all, 'list'] as const,
        list: (params?: any) => [...queryKeys.payments.lists(), params] as const,
        details: () => [...queryKeys.payments.all, 'detail'] as const,
        detail: (id: string | number) => [...queryKeys.payments.details(), id] as const,
    },

    finances: {
        all: ['finances'] as const,
        summary: (params?: any) => [...queryKeys.finances.all, 'summary', params] as const,
    },

    metrics: {
        all: ['metrics'] as const,
        summary: (params?: any) => [...queryKeys.metrics.all, 'summary', params] as const,
    },

    notifications: {
        all: ['notifications'] as const,
        lists: () => [...queryKeys.notifications.all, 'list'] as const,
        list: (params?: any) => [...queryKeys.notifications.lists(), params] as const,
        details: () => [...queryKeys.notifications.all, 'detail'] as const,
        detail: (id: string | number) => [...queryKeys.notifications.details(), id] as const,
    },

    balance: {
        all: ['balance'] as const,
        list: (type: 'persons' | 'businesses' | 'global-persons' | 'global-businesses', params?: any) => [...queryKeys.balance.all, 'list', type, params] as const,
        detail: (type: 'person' | 'business', id: string | number, params?: any) => [...queryKeys.balance.all, 'detail', type, id, params] as const,
    },

    globalSearch: {
        all: ['global-search'] as const,
        query: (query: string, category: string) => [...queryKeys.globalSearch.all, query, category] as const,
    },

    clients: {
        all: ['clients'] as const,
        search: (params: { query: string; categories?: string; owned_by_me?: boolean }) => [...queryKeys.clients.all, 'search', params] as const,
        latestsUsed: (params: { categories?: string; owned_by_me?: boolean }) => [...queryKeys.clients.all, 'latestsUsed', params] as const,
    },

} as const;

// ===============================
// Query Options (Reusable)
// ===============================

// Auth
export const authQueries = {
    me: () => queryOptions({
        queryKey: queryKeys.auth.me(),
        queryFn: () => api.auth.me(),
        staleTime: 5 * 60 * 1000, // 5 minutes
        gcTime: 30 * 60 * 1000, // 30 minutes (was cacheTime in v4)
    }),
};

// Public
export const publicQueries = {
    filters: (params?: any) => queryOptions({
        queryKey: queryKeys.public.filters(params),
        queryFn: () => api.public.getFilters(params),
        staleTime: 2 * 60 * 1000, // 2 minutes
    }),
    services: (params?: any) => queryOptions({
        queryKey: queryKeys.public.services(params),
        queryFn: () => api.public.getServices(params),
        staleTime: 2 * 60 * 1000, // 2 minutes
    }),
    service: (id: string) => queryOptions({
        queryKey: queryKeys.public.service(id),
        queryFn: () => api.public.getService(id),
        staleTime: 2 * 60 * 1000, // 2 minutes
        enabled: !!id,
    }),
    businessBySlug: (slug: string) => queryOptions({
        queryKey: queryKeys.public.businessBySlug(slug),
        queryFn: () => api.public.getBusinessBySlug(slug),
        staleTime: 2 * 60 * 1000, // 2 minutes
        enabled: !!slug,
    }),
    reservation: (slug: string) => queryOptions({
        queryKey: queryKeys.public.reservation(slug),
        queryFn: () => api.public.getReservation(slug),
        staleTime: 1 * 60 * 1000, // 1 minute
        enabled: !!slug,
    }),
    tenantBusiness: (tenantBusinessId: string) => queryOptions({
        queryKey: queryKeys.public.tenantBusiness(tenantBusinessId),
        queryFn: () => api.public.getTenantBusiness(tenantBusinessId),
        staleTime: 2 * 60 * 1000, // 2 minutes
        enabled: !!tenantBusinessId,
    }),
    tenant: (tenantId: string, params?: Record<string, any>) => queryOptions({
        queryKey: [...queryKeys.public.tenant(tenantId), params] as const,
        queryFn: () => api.public.getTenant(tenantId, params),
        staleTime: 2 * 60 * 1000, // 2 minutes
        enabled: !!tenantId,
    }),
};

// Users
export const userQueries = {
    all: (params?: any) => queryOptions({
        queryKey: queryKeys.users.list(params),
        queryFn: () => api.users.getAll(params),
        staleTime: 30 * 1000, // 30 seconds
    }),

    detail: (id: string | number) => queryOptions({
        queryKey: queryKeys.users.detail(id),
        queryFn: () => api.users.getOne(id),
        staleTime: 60 * 1000, // 1 minute
        enabled: !!id,
    }),

    emailHasUser: (email: string) => queryOptions({
        queryKey: queryKeys.users.emailHasUser(email),
        queryFn: () => api.users.emailHasUser(email),
        staleTime: 5 * 60 * 1000, // 5 minutes
        enabled: !!email,
    }),
};

// Templates
export const templateQueries = {
    all: (params?: any) => queryOptions({
        queryKey: queryKeys.templates.list(params),
        queryFn: () => api.templates.getAll(params),
        staleTime: 5 * 60 * 1000, // 5 minutes - templates change less frequently
    }),

    detail: (id: string | number) => queryOptions({
        queryKey: queryKeys.templates.detail(id),
        queryFn: () => api.templates.getOne(id),
        staleTime: 5 * 60 * 1000,
        enabled: !!id,
    }),
};

// Businesses
export const businessQueries = {
    all: (params?: any) => queryOptions({
        queryKey: queryKeys.businesses.list(params),
        queryFn: () => api.businesses.getAll(params),
        staleTime: 2 * 60 * 1000, // 2 minutes
    }),

    detail: (id: string | number) => queryOptions({
        queryKey: queryKeys.businesses.detail(id),
        queryFn: () => api.businesses.getOne(id),
        staleTime: 2 * 60 * 1000,
        enabled: !!id,
    }),

    home: () => queryOptions({
        queryKey: queryKeys.businesses.home(),
        queryFn: () => api.businesses.getMyBusinessesHome(),
        staleTime: 30 * 1000,
    }),

    vendorsWithPublicServices: () => queryOptions({
        queryKey: ['businesses', 'vendors-with-public-services'],
        queryFn: () => api.businesses.getVendorsWithPublicServices(),
        staleTime: 5 * 60 * 1000,
    }),
};

export const tenantBusinessQueries = {
    all: (params?: any) => queryOptions({
        queryKey: queryKeys.tenantBusinesses.list(params),
        queryFn: () => api.tenantBusinesses.getAll(params),
        staleTime: 2 * 60 * 1000, // 2 minutes
    }),

    detail: (id: string | number) => queryOptions({
        queryKey: queryKeys.tenantBusinesses.detail(id),
        queryFn: () => api.tenantBusinesses.getOne(id),
        staleTime: 2 * 60 * 1000,
        enabled: !!id,
    }),
};


// Reservations
export const reservationQueries = {
    all: (params?: any) => queryOptions({
        queryKey: queryKeys.reservations.list(params),
        queryFn: () => api.reservations.getAll(params),
        staleTime: 30 * 1000, // 30 seconds - frequently changing
    }),

    agenda: (params?: any) => queryOptions({
        queryKey: queryKeys.reservations.agenda(params),
        queryFn: () => api.reservations.agenda(params),
        staleTime: 30 * 1000,
    }),

    assignedCoordinator: (params?: any) => queryOptions({
        queryKey: queryKeys.reservations.assignedCoordinator(params),
        queryFn: () => api.reservations.getAssignedCoordinator(params),
        staleTime: 30 * 1000,
    }),

    detail: (id: string | number) => queryOptions({
        queryKey: queryKeys.reservations.detail(id),
        queryFn: () => api.reservations.getOne(id),
        staleTime: 30 * 1000,
        enabled: !!id,
    }),
};

// Deleted Reservations
export const deletedReservationQueries = {
    all: (params?: any) => queryOptions({
        queryKey: queryKeys.deletedReservations.list(params),
        queryFn: () => api.deletedReservations.getAll(params),
        staleTime: 30 * 1000,
    }),

    detail: (id: string | number) => queryOptions({
        queryKey: queryKeys.deletedReservations.detail(id),
        queryFn: () => api.deletedReservations.getOne(id),
        staleTime: 30 * 1000,
        enabled: !!id,
    }),
};

// Services
export const serviceQueries = {
    all: (params?: any) => queryOptions({
        queryKey: queryKeys.services.list(params),
        queryFn: () => api.services.getAll(params),
        staleTime: 60 * 1000, // 1 minute
    }),

    detail: (id: string | number) => queryOptions({
        queryKey: queryKeys.services.detail(id),
        queryFn: () => api.services.getOne(id),
        staleTime: 60 * 1000,
        enabled: !!id,
    }),

    categoriesPrivate: () => queryOptions({
        queryKey: queryKeys.serviceCategories.list(),
        queryFn: () => api.services.getCategoriesPrivate(),
        staleTime: 60 * 1000,
    }),

    locations: (category?: string) => queryOptions({
        queryKey: queryKeys.services.locations(category),
        queryFn: () => api.services.getLocations(category),
        staleTime: 60 * 1000,
    }),
};

// Service Sell Prices
export const serviceSellPriceQueries = {
    all: (params?: any) => queryOptions({
        queryKey: queryKeys.serviceSellPrices.list(params),
        queryFn: () => api.serviceSellPrices.getAll(params),
        staleTime: 60 * 1000, // 1 minute
    }),

    detail: (id: string | number) => queryOptions({
        queryKey: queryKeys.serviceSellPrices.detail(id),
        queryFn: () => api.serviceSellPrices.getOne(id),
        staleTime: 60 * 1000,
        enabled: !!id,
    }),

    active: () => queryOptions({
        queryKey: queryKeys.serviceSellPrices.active(),
        queryFn: () => api.serviceSellPrices.getActive(),
        staleTime: 60 * 1000,
    }),
};

// Service Groups
export const serviceGroupQueries = {
    all: (params?: any) => queryOptions({
        queryKey: queryKeys.serviceGroups.list(params),
        queryFn: () => api.serviceGroups.getAll(params),
        staleTime: 60 * 1000, // 1 minute
    }),

    detail: (id: string | number) => queryOptions({
        queryKey: queryKeys.serviceGroups.detail(id),
        queryFn: () => api.serviceGroups.getOne(id),
        staleTime: 60 * 1000,
        enabled: !!id,
    }),
};

// Products
export const productQueries = {
    all: (params?: any) => queryOptions({
        queryKey: queryKeys.products.list(params),
        queryFn: () => api.products.getAll(params),
        staleTime: 60 * 1000,
    }),

    detail: (id: string | number) => queryOptions({
        queryKey: queryKeys.products.detail(id),
        queryFn: () => api.products.getOne(id),
        staleTime: 60 * 1000,
        enabled: !!id,
    }),
};

// Service Providers
export const serviceProviderQueries = {
    all: (params?: any) => queryOptions({
        queryKey: queryKeys.serviceProviders.list(params),
        queryFn: () => api.serviceProviders.getAll(params),
        staleTime: 60 * 1000, // 1 minute
    }),

    detail: (id: string | number) => queryOptions({
        queryKey: queryKeys.serviceProviders.detail(id),
        queryFn: () => api.serviceProviders.getOne(id),
        staleTime: 60 * 1000,
        enabled: !!id,
    }),
};

// Global Search
export const globalSearchQueries = {
    query: (query: string, category: string) => queryOptions({
        queryKey: queryKeys.globalSearch.query(query, category),
        queryFn: () => api.globalSearch.get(query, category),
        staleTime: 5 * 60 * 1000,
        enabled: !!query && !!category,
    }),
};


// Service Bookings
export const serviceBookingQueries = {
    all: (params?: any) => queryOptions({
        queryKey: queryKeys.serviceBookings.list(params),
        queryFn: () => api.services.getBookings(params),
        staleTime: 30 * 1000,
    }),

    byService: (params?: any) => queryOptions({
        queryKey: queryKeys.serviceBookings.list(params),
        queryFn: () => api.services.getBookingsByService(params),
        staleTime: 30 * 1000,
    }),

    detail: (id: string | number) => queryOptions({
        queryKey: queryKeys.serviceBookings.detail(id),
        queryFn: () => api.reservations.getServiceBooking(id),
        staleTime: 30 * 1000,
        enabled: !!id,
    }),

    assignedBookings: (params?: any) => queryOptions({
        queryKey: queryKeys.serviceBookings.assigned(params),
        queryFn: () => api.serviceBookings.getAssigned(params),
        staleTime: 30 * 1000,
    }),

    assignedServiceOrders: (params?: any) => queryOptions({
        queryKey: queryKeys.serviceBookings.assignedServiceOrders(params),
        queryFn: () => api.serviceOrders.getAssigned(params),
        staleTime: 30 * 1000,
    }),
};

export const serviceBookingInventoryLinkQueries = {
    list: (params?: any) => queryOptions({
        queryKey: queryKeys.serviceBookingInventoryLinks.list(params),
        queryFn: () => api.serviceBookingInventoryLinks.get(params),
        staleTime: 30 * 1000,
    }),
};

export const serviceOrderQueries = {
    serviceOrders: (params?: any) => queryOptions({
        queryKey: queryKeys.serviceOrders.list(params),
        queryFn: () => api.serviceOrders.getServiceBookings(params),
        staleTime: 30 * 1000,
    }),

    serviceOrder: (id: string | number) => queryOptions({
        queryKey: queryKeys.serviceOrders.detail(id),
        queryFn: () => api.serviceOrders.getServiceOrder(id),
        staleTime: 30 * 1000,
        enabled: !!id,
    }),
}

// Persons
export const personQueries = {
    all: (params?: any) => queryOptions({
        queryKey: queryKeys.persons.list(params),
        queryFn: () => api.persons.getAll(params),
        staleTime: 60 * 1000,
    }),

    detail: (id: string | number) => queryOptions({
        queryKey: queryKeys.persons.detail(id),
        queryFn: () => api.persons.getOne(id),
        staleTime: 60 * 1000,
        enabled: !!id,
    }),

    allStaff: () => queryOptions({
        queryKey: queryKeys.persons.staffList(),
        queryFn: () => api.persons.getAllStaff(),
        staleTime: 2 * 60 * 1000, // 2 minutes - staff doesn't change often
    }),
};

export const clientQueries = {
    search: (params: { query: string; categories?: string; owned_by_me?: boolean }) => queryOptions({
        queryKey: queryKeys.clients.search(params),
        queryFn: () => api.clients.search(params),
        staleTime: 30 * 1000,
        enabled: !!params.query && params.query.trim().length >= 2,
    }),
    latestsUsed: (params: { categories?: string; owned_by_me?: boolean }) => queryOptions({
        queryKey: queryKeys.clients.latestsUsed(params),
        queryFn: () => api.clients.latestsUsed(params),
        staleTime: 30 * 1000,
    }),
};

// Inventory Items
export const inventoryItemQueries = {
    all: (params?: any) => queryOptions({
        queryKey: queryKeys.inventoryItems.list(params),
        queryFn: () => api.inventoryItems.getAll(params),
        staleTime: 60 * 1000, // 1 minute
    }),

    detail: (id: string | number) => queryOptions({
        queryKey: queryKeys.inventoryItems.detail(id),
        queryFn: () => api.inventoryItems.getOne(id),
        staleTime: 60 * 1000,
        enabled: !!id,
    }),

    status: (params?: { start_date?: string; end_date?: string; days?: number }) => queryOptions({
        queryKey: queryKeys.inventoryItems.status(params),
        queryFn: () => api.inventoryItems.getStatus(params),
        staleTime: 30 * 1000, // 30 seconds - status changes frequently
    }),
};



// Payments
export const paymentQueries = {
    all: (params?: any) => queryOptions({
        queryKey: queryKeys.payments.list(params),
        queryFn: () => api.payments.getAll(params),
        staleTime: 30 * 1000,
    }),

    detail: (id: string | number) => queryOptions({
        queryKey: queryKeys.payments.detail(id),
        queryFn: () => api.payments.getOne(id),
        staleTime: 30 * 1000,
        enabled: !!id,
    }),
};

// Finances
export const financesQueries = {
    summary: (params?: any) => queryOptions({
        queryKey: queryKeys.finances.summary(params),
        queryFn: () => api.finances.getSummary(params),
        staleTime: 60 * 1000,
    }),
};

// Metrics
export const metricsQueries = {
    summary: (params?: any) => queryOptions({
        queryKey: queryKeys.metrics.summary(params),
        queryFn: () => api.metrics.getMetrics(params),
        staleTime: 60 * 1000,
    }),
};

// Notifications
export const notificationQueries = {
    all: (params?: any) => queryOptions({
        queryKey: queryKeys.notifications.list(params),
        queryFn: () => api.notifications.getAll(params),
        staleTime: 30 * 1000,
        refetchInterval: 30000, // Poll every 30s
    }),

    detail: (id: string | number) => queryOptions({
        queryKey: queryKeys.notifications.detail(id),
        queryFn: () => api.notifications.getOne(id),
        staleTime: 30 * 1000,
        enabled: !!id,
    }),
};

// Balance
export const balanceQueries = {
    businesses: (params?: any) => queryOptions({
        queryKey: queryKeys.balance.list('global-businesses', params),
        queryFn: () => api.balance.getGlobalBusinesses(params),
        staleTime: 60 * 1000,
    }),
    personDetail: (id: string | number, params?: any) => queryOptions({
        queryKey: queryKeys.balance.detail('person', id, params),
        queryFn: () => api.balance.getPersonBalance(id, params),
        staleTime: 60 * 1000,
        enabled: !!id,
    }),
    businessDetail: (id: string | number, params?: any) => queryOptions({
        queryKey: queryKeys.balance.detail('business', id, params),
        queryFn: () => api.balance.getBusinessBalance(id, params),
        staleTime: 60 * 1000,
        enabled: !!id,
    }),
    issues: () => queryOptions({
        queryKey: ['balance', 'issues'] as const,
        queryFn: () => api.balance.getIssues(),
        staleTime: 60 * 1000,
    }),
};

// ===============================
// Custom Hooks
// ===============================

// Auth Hooks
export const useMe = () => useQuery<ApiResponse<User>, Error>(authQueries.me());

// Public Hooks
export const usePublicServices = (params?: any, options?: any) => {
    const queryConfig = {
        ...publicQueries.services(params),
        ...options,

    };
    return useQuery<ApiResponse<Service[]>, Error>(queryConfig);
};
export const usePublicService = (id: string, options?: any) => {
    return useQuery<ApiResponse<{ service: Service, client: Business, owner: Tenant }>, Error>({
        ...publicQueries.service(id),
        ...options
    });
};
export const usePublicReservation = (slug: string) => useQuery(publicQueries.reservation(slug));
export const usePublicFilters = (params?: any, options?: any) => useQuery<ApiResponse<any>, Error>({
    ...publicQueries.filters(params),
    ...options
});
export const usePublicBusinessBySlug = (slug: string) => useQuery(publicQueries.businessBySlug(slug));

export const usePublicTenantBusiness = (tenantBusinessId: string) => useQuery<ApiResponse<TenantBusiness>, Error>(publicQueries.tenantBusiness(tenantBusinessId));
export const usePublicTenant = (tenantId: string, params?: Record<string, any>) => useQuery<ApiResponse<Tenant>, Error>(publicQueries.tenant(tenantId, params));
export const usePublicServicePrice = (id: string | number | undefined, params?: any, options?: any) => useQuery<ApiResponse<any>, Error>({
    queryKey: ['public', 'service-price', id, params],
    queryFn: () => api.public.getServicePrice(id!, params),
    enabled: !!id,
    ...options
});

// User Hooks
export const useUsers = (params?: any) => useQuery(userQueries.all(params));
export const useUser = (id: string | number) => useQuery(userQueries.detail(id));
export const useEmailHasUser = (email: string) => useQuery(userQueries.emailHasUser(email));

// Tenant Hooks
export const useTenant = (id: string | number | undefined) => useQuery<ApiResponse<Tenant>, Error>({
    queryKey: queryKeys.tenants.detail(id || ''),
    queryFn: () => api.tenants.getOne(id!),
    staleTime: 5 * 60 * 1000, // 5 minutes
    enabled: !!id,
});

// Business Hooks
export const useBusinesses = (params?: any, options?: any) => useQuery<ApiResponse<Business[]>, Error>({
    ...businessQueries.all(params),
    ...options
});
export const useMyBusinesses = (params?: any, options?: any) => useQuery<ApiResponse<Business[]>, Error>({
    ...businessQueries.all({
        owned_by_me: true
    }),
    ...options
});
export const useMyBusinessesHome = (options?: any) => useQuery<ApiResponse<any[]>, Error>({
    ...businessQueries.home(),
    ...options
});
export const useBusiness = (id: string | number | undefined | null, options?: any) => useQuery<ApiResponse<Business>, Error>({
    ...businessQueries.detail(id !== undefined && id !== null ? id : ''),
    ...options
});
export const useVendorsWithPublicServices = (options?: any) => useQuery<ApiResponse<any[]>, Error>({
    ...businessQueries.vendorsWithPublicServices(),
    ...options
});

export const useMergeBusiness = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ targetBusinessId, sourceBusinessId }: { targetBusinessId: number | string; sourceBusinessId: number | string }) =>
            api.businesses.merge(targetBusinessId, sourceBusinessId),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: queryKeys.businesses.all });
            queryClient.invalidateQueries({ queryKey: ['reservations'] });
            queryClient.invalidateQueries({ queryKey: ['payments'] });
            queryClient.invalidateQueries({ queryKey: ['service-bookings'] });
            notify.success('operation.success', 'business_merged_successfully');
        },
        onError: (err: any) => {
            notify.error('operation.error', err?.message || 'error_merging_business');
        }
    });
};

export const useTenantBusinesses = (params?: any, options?: any) => useQuery<ApiResponse<TenantBusiness[]>, Error>({
    ...tenantBusinessQueries.all(params),
    ...options
});
export const useTenantBusiness = (params?: any, options?: any) => useQuery<ApiResponse<TenantBusiness>, Error>({
    ...tenantBusinessQueries.detail(params),
    ...options
});

export const useGetTenantBusiness = (tenantId: number | string | undefined | null, businessId: number | string | undefined | null, options?: any) => useQuery<ApiResponse<TenantBusiness>, Error>({
    queryKey: ['tenant-businesses', 'getTenantBusiness', tenantId, businessId],
    queryFn: async () => {
        const res = await api.tenantBusinesses.getTenantBusiness(tenantId!, businessId!);
        return res;
    },
    enabled: !!tenantId && !!businessId,
    ...options
});

export const useTariffEmailLogs = (id: string | number | undefined, options?: any) => useQuery<ApiResponse<any[]>, Error>({
    queryKey: queryKeys.tenantBusinesses.emailLogs(id || ''),
    queryFn: () => api.tenantBusinesses.getTariffEmailLogs(id!),
    enabled: !!id,
    ...options
});

export const useAllTariffEmailLogs = (options?: any) => useQuery<ApiResponse<any[]>, Error>({
    queryKey: queryKeys.tenantBusinesses.allEmailLogs(),
    queryFn: () => api.tenantBusinesses.getAllTariffEmailLogs(),
    ...options
});


// Reservation Hooks
export const useReservations = (params?: any, options?: any) => useQuery<ApiResponse<Reservation[]>, Error>({
    ...reservationQueries.all(params),
    ...options
});
export interface ReservationsTotalSold {
    totals: Record<string, number>;
    count: number;
    reservations?: any[];
}

export const useReservationsTotalSold = (params?: any, options?: any) => useQuery<ReservationsTotalSold, Error>({
    queryKey: ['reservations', 'total-sold', params],
    queryFn: async () => {
        const res = await api.reservations.getTotalSold(params);
        return res.data;
    },
    ...options
});
export const useAgenda = (params?: any, options?: any) => useQuery<ApiResponse<any[]>, Error>({
    ...reservationQueries.agenda(params),
    ...options
});
export const useAssignedCoordinatorReservations = (params?: any, options?: any) => useQuery<ApiResponse<Reservation[]>, Error>({
    ...reservationQueries.assignedCoordinator(params),
    ...options
});
export const useReservation = (id: string | number) => useQuery<ApiResponse<Reservation>, Error>(reservationQueries.detail(id));
export const useDeletedReservations = (params?: any, options?: any) => useQuery<ApiResponse<DeletedReservation[]>, Error>({
    ...deletedReservationQueries.all(params),
    ...options
});
export const useDeletedReservation = (id: string | number | undefined, options?: any) => useQuery<ApiResponse<DeletedReservation>, Error>({
    ...deletedReservationQueries.detail(id || ''),
    enabled: !!id,
    ...options
});
export const useReservationErrorState = (id: string | number, options?: any) => {
    return useQuery<any, Error>({
        queryKey: ['reservations', 'error-state', id],
        queryFn: () => api.reservations.getErrorState(id),
        enabled: !!id,
        ...options
    });
};
export const useUnpaidReservations = (clientId?: number, clientType?: string, options?: any) => {
    return useQuery<ApiResponse<Reservation[]>, Error>({
        queryKey: ['reservations', 'unpaid', clientId, clientType],
        queryFn: () => api.reservations.getUnpaid(clientId!, clientType!),
        enabled: !!clientId && !!clientType && options?.enabled !== false,
        staleTime: 30 * 1000,
        ...options
    });
};
export const useUnpaidServiceBookings = (providerId?: number, options?: any) => {
    return useQuery<ApiResponse<ServiceBooking[]>, Error>({
        queryKey: ['service-bookings', 'unpaid-costs', providerId],
        queryFn: () => api.serviceBookings.getUnpaidCosts(providerId!),
        enabled: !!providerId && options?.enabled !== false,
        staleTime: 30 * 1000,
        ...options
    });
};

// Service Hooks
export const useServices = (params?: any, options?: any) => useQuery<ApiResponse<Service[]>, Error>({
    ...serviceQueries.all(params),
    ...options,

});
export const useService = (id: string | number) => useQuery<ApiResponse<Service>, Error>(serviceQueries.detail(id));

export const useServiceCategoriesPrivate = (options?: any) => useQuery<ApiResponse<ServiceCategory[]>, Error>({
    ...serviceQueries.categoriesPrivate(),
    ...options
});

export const useServiceLocations = (category?: string, options?: any) => useQuery<ApiResponse<string[]>, Error>({
    ...serviceQueries.locations(category),
    ...options,
});

export const useUpdateServiceCategoryRank = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async ({ id, rank }: { id: string | number; rank: number | null }) => {
            return api.put('/services-categories', Number(id), { rank });
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: queryKeys.serviceCategories.lists() });
            notify.success('Success', 'Rank updated successfully');
        },
        onError: () => {
            notify.error('Error', 'Failed to update rank');
        }
    });
};

export const useServicesByProvider = (businessId: string | number, options?: any) => useQuery<ApiResponse<Service[]>, Error>({
    queryKey: queryKeys.services.byProvider(businessId),
    queryFn: () => api.services.getByProvider(businessId),
    ...options
});

export const useServiceFilterOptions = (params?: any, options?: any) => useQuery<ApiResponse<{ where: string[], from: string[], to: string[], routes: Record<string, string[]>, filters: Record<string, any[]> }>, Error>({
    queryKey: queryKeys.services.filterOptions(params),
    queryFn: () => api.services.getFilterOptions(params),
    staleTime: 5 * 60 * 1000,
    ...options
});

// Service Group Hooks
export const useServiceGroups = (params?: any, options?: any) => useQuery<ApiResponse<ServiceGroup[]>, Error>({
    ...serviceGroupQueries.all(params),
    ...options
});
export const useServiceGroup = (id: string | number) => useQuery<ApiResponse<ServiceGroup>, Error>(serviceGroupQueries.detail(id));

// Product Hooks
export const useProducts = (params?: any, options?: any) => useQuery<ApiResponse<Product[]>, Error>({
    ...productQueries.all(params),
    ...options
});
export const useProduct = (id: string | number, options?: any) => useQuery<ApiResponse<Product>, Error>({
    ...productQueries.detail(id),
    ...options
});

// Service Provider Hooks
export const useServiceProviders = (params?: any, options?: any) => useQuery<ApiResponse<ServiceProvider[]>, Error>({
    ...serviceProviderQueries.all(params),
    ...options
});
export const useServiceProvider = (id: string | number) => useQuery<ApiResponse<ServiceProvider>, Error>(serviceProviderQueries.detail(id));

export const useGlobalSearch = (query: string, category: string, options?: any) => useQuery<ApiResponse<any>, Error>({
    ...globalSearchQueries.query(query, category),
    ...options
});

// Service Booking Hooks
export const useServiceBookingsByService = (params?: any, options?: any) => useQuery<ApiResponse<ServiceBooking[]>, Error>({
    ...serviceBookingQueries.byService(params),
    ...options
});
export const useServiceBookings = (params?: any, options?: any) => useQuery<ApiResponse<ServiceBooking[]>, Error>({
    ...serviceBookingQueries.all(params),
    ...options
});
export const useAssignedBookings = (params?: any, options?: any) => useQuery<ApiResponse<ServiceBooking[]>, Error>({
    ...serviceBookingQueries.assignedBookings(params),
    ...options
});
export const useAssignedServiceOrders = (params?: any, options?: any) => useQuery<ApiResponse<ServiceOrder[]>, Error>({
    ...serviceBookingQueries.assignedServiceOrders(params),
    ...options
});
export const useServiceBooking = (id: string | number, options?: any) => useQuery<ApiResponse<ServiceBooking>, Error>({
    ...serviceBookingQueries.detail(id),
    ...options
});
export const useServiceAvailableLimits = (params?: any, options?: { enabled?: boolean }) => useQuery({
    queryKey: queryKeys.services.availableLimits(params),
    queryFn: () => api.services.getAvailableLimits(params),
    enabled: options?.enabled !== undefined ? options.enabled : !!params,
});

export const useServiceBookingsLimits = (params?: any, options?: { enabled?: boolean }) => useQuery({
    queryKey: queryKeys.services.bookingsLimits(params),
    queryFn: () => api.services.getBookingsLimits(params),
    enabled: options?.enabled !== undefined ? options.enabled : !!params,
});

export const useServiceInventory = (serviceId: string | number, options?: { enabled?: boolean }) => useQuery({
    queryKey: queryKeys.services.inventory(serviceId),
    queryFn: () => api.services.getInventory(serviceId),
    enabled: options?.enabled !== undefined ? options.enabled : !!serviceId,
});

export const useUpdateServiceInventory = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ serviceId, ...data }: { serviceId: string | number;[key: string]: any }) =>
            api.services.updateInventory(serviceId, data),
        onSuccess: (_res, variables) => {
            queryClient.invalidateQueries({ queryKey: queryKeys.services.inventory(variables.serviceId) });
            queryClient.invalidateQueries({ queryKey: queryKeys.services.all });
        }
    });
};

export const useServiceBookingInventoryLinks = (params?: any, options?: any) => useQuery<ApiResponse<any[]>, Error>({
    ...serviceBookingInventoryLinkQueries.list(params),
    ...options
});

export const useBalanceBusinesses = (params?: any) => useQuery<ApiResponse<ClientBalanceGroup[]>, Error>(balanceQueries.businesses(params));
export const useBalance = (params?: any) => {
    const id = params?.find_by_id;
    const isPerson = params?.find_by_id_type === 'person';
    const start_day = params?.start_day;
    const end_day = params?.end_day;

    const queryParams: any = {};
    if (start_day) queryParams.start_day = start_day;
    if (end_day) queryParams.end_day = end_day;

    return useQuery<ApiResponse<ClientBalanceGroup[]>, Error>({
        ...(isPerson ? balanceQueries.personDetail(id, queryParams) : balanceQueries.businessDetail(id, queryParams)),
        enabled: !!id
    });
};
export const useIssues = () => useQuery<ApiResponse<IssuesData>, Error>(balanceQueries.issues());

export const useCreateServiceBookingInventoryLink = () => {
    const queryClient = useQueryClient();
    return useMutation<ApiResponse<any>, Error, { service_booking_id: number | string, inventory_item_id: number | string }>({
        mutationFn: (data) => api.serviceBookingInventoryLinks.create(data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: queryKeys.serviceBookingInventoryLinks.all });
            // Invalidate service bookings for Agenda Page and others
            queryClient.invalidateQueries({ queryKey: queryKeys.serviceBookings.all });
            queryClient.invalidateQueries({ queryKey: queryKeys.serviceOrders.all });
        },
    });
};

export const useDeleteServiceBookingInventoryLink = () => {
    const queryClient = useQueryClient();
    return useMutation<ApiResponse<any>, Error, { bookingId: number | string, itemId: number | string }>({
        mutationFn: ({ bookingId, itemId }) => api.serviceBookingInventoryLinks.delete(bookingId, itemId),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: queryKeys.serviceBookingInventoryLinks.all });
            // Invalidate service bookings for Agenda Page and others
            queryClient.invalidateQueries({ queryKey: queryKeys.serviceBookings.all });
            queryClient.invalidateQueries({ queryKey: queryKeys.serviceOrders.all });
        },
    });
};

// Service Order Hooks
export const useServiceOrders = (params?: any, options?: any) => useQuery<ApiResponse<ServiceOrder[]>, Error>({
    ...serviceOrderQueries.serviceOrders(params),
    ...options
});
export const useServiceOrder = (id: string | number, options?: any) => useQuery<ApiResponse<ServiceOrder>, Error>({
    ...serviceOrderQueries.serviceOrder(id),
    ...options
});

// Person Hooks
export const usePersons = (params?: any, options?: any): UseQueryResult<ApiResponse<Person[]>, Error> => useQuery({
    ...personQueries.all(params),
    ...options
});
export const usePerson = (id: string | number) => useQuery<ApiResponse<Person>, Error>(personQueries.detail(id));
export const useReservationsAsPax = (personId: string | number, options?: any) => useQuery<ApiResponse<Reservation[]>, Error>({
    queryKey: ['persons', personId, 'reservations-as-pax'],
    queryFn: () => api.persons.getReservationsAsPax(personId),
    enabled: !!personId,
    ...options
});
export const useClientBusinessReservations = (businessId: string | number, options?: any) => useQuery<ApiResponse<Reservation[]>, Error>({
    queryKey: ['businesses', businessId, 'reservations-as-client'],
    queryFn: () => api.businesses.getClientReservations(businessId),
    enabled: !!businessId,
    ...options
});
export const useStaff = (params?: any, options?: any) => useQuery({
    ...personQueries.allStaff(),
    ...options
});

export const useClientSearch = (params: { query: string; categories?: string; owned_by_me?: boolean }, options?: any) => useQuery<ApiResponse<any>, Error>({
    ...clientQueries.search(params),
    ...options
});

export const useClientLatestUsed = (params: { categories?: string; owned_by_me?: boolean }, options?: any) => useQuery<ApiResponse<any>, Error>({
    ...clientQueries.latestsUsed(params),
    ...options
});

// Inventory Item Hooks
export const useInventoryItems = (params?: any, options?: any): UseQueryResult<ApiResponse<InventoryItem[]>, Error> => useQuery({
    ...inventoryItemQueries.all(params),
    ...options
});
export const useInventoryItem = (id: string | number, options?: any) => useQuery<ApiResponse<InventoryItem>, Error>({
    ...inventoryItemQueries.detail(id),
    ...options
});
export const useInventoryStatus = (params?: any, options?: any) => useQuery<ApiResponse<InventoryStatus[]>, Error>({
    ...inventoryItemQueries.status(params),
    ...options
});




// Finances Hooks
type FinancesSummary = {
    totalIncome: Record<string, number>;
    confirmedIncome: Record<string, number>;
    pendingIncome: Record<string, number>;
    cancelledIncome: Record<string, number>;
    totalEgress: Record<string, number>;
    confirmedEgress: Record<string, number>;
    pendingEgress: Record<string, number>;
    cancelledEgress: Record<string, number>;
    totalIncomeByCategory: Record<string, Record<string, number>>;
    confirmedIncomeByCategory: Record<string, Record<string, number>>;
    pendingIncomeByCategory: Record<string, Record<string, number>>;
    cancelledIncomeByCategory: Record<string, Record<string, number>>;
    totalEgressByCategory: Record<string, Record<string, number>>;
    confirmedEgressByCategory: Record<string, Record<string, number>>;
    pendingEgressByCategory: Record<string, Record<string, number>>;
    cancelledEgressByCategory: Record<string, Record<string, number>>;
    totalCount: number;
    confirmedCount: number;
    pendingCount: number;
    cancelledCount: number;
};

export const useFinancesSummary = (params?: any, options?: any) => useQuery<ApiResponse<FinancesSummary>, Error>({
    ...financesQueries.summary(params),
    ...options
});

export const useMetrics = (params?: any, options?: any) => useQuery<ApiResponse<any>, Error>({
    ...metricsQueries.summary(params),
    ...options
});

// Payment Hooks
export const usePayments = (params?: any, options?: any) => useQuery<ApiResponse<Payment[]>, Error>({
    ...paymentQueries.all(params),
    ...options
});
export const usePayment = (id: string | number) => useQuery<ApiResponse<Payment>, Error>(paymentQueries.detail(id));


// ===============================
// Mutation Hooks
// ===============================

// User Mutations
export const useCreateUser = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (userData: Partial<User>) => api.users.save(userData),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: queryKeys.users.all });
            notify.success('operation.success', 'operation.save_settings.success');
        },
        onError: (error: any) => {
            notify.error('operation.error', 'operation.save_settings.error');
        }
    });
};

export const useUpdateUser = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (userData: Partial<User>) => api.users.save(userData),
        onSuccess: (_, variables) => {
            // Invalidate and refetch user lists
            queryClient.invalidateQueries({ queryKey: queryKeys.users.all });
            // Invalidate specific user detail
            queryClient.invalidateQueries({ queryKey: queryKeys.users.detail(variables.id) });
        },
    });
};

export const useDeleteUser = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (id: string | number) => api.users.delete(id),
        onSuccess: (_, deletedId) => {
            queryClient.invalidateQueries({ queryKey: queryKeys.users.all });
            queryClient.removeQueries({ queryKey: queryKeys.users.detail(deletedId) });
        },
    });
};

// Template Mutations
export const useCreateTemplate = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (templateData: Partial<Template>) => api.templates.save(templateData),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: queryKeys.templates.all });
        },
    });
};

export const useUpdateTemplate = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (templateData: Template) => api.templates.save(templateData),
        onSuccess: (_, variables) => {
            queryClient.invalidateQueries({ queryKey: queryKeys.templates.all });
            queryClient.invalidateQueries({ queryKey: queryKeys.templates.detail(variables.id) });
        },
    });
};

export const useDeleteTemplate = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (id: string | number) => api.templates.delete(id),
        onSuccess: (_, deletedId) => {
            queryClient.invalidateQueries({ queryKey: queryKeys.templates.all });
            queryClient.removeQueries({ queryKey: queryKeys.templates.detail(deletedId) });
        },
    });
};

// Business Mutations
export const useCreateBusiness = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (businessData: Partial<Business>) => api.businesses.save(businessData),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: queryKeys.businesses.all });
        },
    });
};

export const useUpdateBusiness = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (businessData: Partial<Business>) => api.businesses.save(businessData),
        onSuccess: (_, variables) => {
            queryClient.invalidateQueries({ queryKey: queryKeys.businesses.all });
            queryClient.invalidateQueries({ queryKey: queryKeys.businesses.detail(variables.id) });
            queryClient.invalidateQueries({ queryKey: queryKeys.persons.all });
        },
    });
};

export const useDeleteBusiness = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (id: string | number) => api.businesses.delete(id),
        onSuccess: (_, deletedId) => {
            queryClient.invalidateQueries({ queryKey: queryKeys.businesses.all });
            queryClient.removeQueries({ queryKey: queryKeys.businesses.detail(deletedId) });
        },
    });
};




// Tenant Business Mutations
export const useCreateTenantBusiness = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (tenantBusinessData: Partial<TenantBusiness>) => api.tenantBusinesses.save(tenantBusinessData),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: queryKeys.tenantBusinesses.all });
        },
    });
};

export const useUpdateTenantBusiness = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (tenantBusinessData: Partial<TenantBusiness>) => api.tenantBusinesses.save(tenantBusinessData),
        onSuccess: (_, variables) => {
            queryClient.invalidateQueries({ queryKey: queryKeys.tenantBusinesses.all });
            queryClient.invalidateQueries({ queryKey: queryKeys.tenantBusinesses.detail(variables.id) });
            // Also invalidate businesses queries since tenant business data affects business views
            queryClient.invalidateQueries({ queryKey: queryKeys.businesses.all });
        },
    });
};

export const useDeleteTenantBusiness = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (id: string | number) => api.tenantBusinesses.delete(id),
        onSuccess: (_, deletedId) => {
            queryClient.invalidateQueries({ queryKey: queryKeys.tenantBusinesses.all });
            queryClient.removeQueries({ queryKey: queryKeys.tenantBusinesses.detail(deletedId) });
        },
    });
};


// Reservation Mutations
export const useCreateReservation = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (reservationData: Partial<Reservation>) => api.reservations.save(reservationData),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: queryKeys.reservations.all });
        },
    });
};

export const useUpdateReservation = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (reservationData: Partial<Reservation>) => api.reservations.save(reservationData),
        onSuccess: (_, variables) => {
            queryClient.invalidateQueries({ queryKey: queryKeys.reservations.all });
            queryClient.invalidateQueries({ queryKey: queryKeys.reservations.detail(variables.id) });
        },
    });
};

export const useAssignReservationClient = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ id, clientId, clientType }: { id: number | string; clientId: number | null; clientType: 'person' | 'business' | null }) =>
            api.reservations.assignClient(id, clientId, clientType),
        onSuccess: (_, variables) => {
            queryClient.invalidateQueries({ queryKey: queryKeys.reservations.all });
            queryClient.invalidateQueries({ queryKey: queryKeys.reservations.detail(variables.id) });
        },
    });
};

export const useDuplicateReservation = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (variables: number | string | { id: number | string; client_id?: number | string; client_type?: string }) => {
            if (typeof variables === 'object' && variables !== null) {
                const clientId = variables.client_id ? Number(variables.client_id) : undefined;
                return api.reservations.duplicate(variables.id, { client_id: clientId, client_type: variables.client_type });
            }
            return api.reservations.duplicate(variables as string | number);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: queryKeys.reservations.all });
            notify.success('operation.success', 'operation.duplicate_reservation.success');
        },
        onError: (error: any) => {
            notify.error('operation.error', 'operation.duplicate_reservation.error');
        }
    });
};

export const useDeleteReservation = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (id: string | number) => api.reservations.delete(id),
        onSuccess: (_, deletedId) => {
            queryClient.invalidateQueries({ queryKey: queryKeys.reservations.all });
            queryClient.removeQueries({ queryKey: queryKeys.reservations.detail(deletedId) });
            queryClient.invalidateQueries({ queryKey: queryKeys.notifications.all });
        },
    });
};

// Service Mutations with Optimistic Updates
export const useCreateService = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (serviceData: Partial<Service>) => api.services.save(serviceData),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: queryKeys.services.all });
        },
    });
};

export const useUpdateService = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (serviceData: Partial<Service>) => api.services.save(serviceData),
        // Optimistic update
        onMutate: async (newService) => {
            // Cancel any outgoing refetches
            await queryClient.cancelQueries({ queryKey: queryKeys.services.detail(newService.id) });

            // Snapshot the previous value
            const previousService = queryClient.getQueryData(queryKeys.services.detail(newService.id));

            // Optimistically update to the new value
            queryClient.setQueryData(queryKeys.services.detail(newService.id), (old: any) => {
                if (!old || !old.data) return old;
                return {
                    ...old,
                    data: {
                        ...old.data,
                        ...newService,
                    }
                };
            });

            // Return a context object with the snapshotted value
            return { previousService };
        },
        onError: (err, newService, context) => {
            // If the mutation fails, use the context returned from onMutate to roll back
            if (context?.previousService) {
                queryClient.setQueryData(queryKeys.services.detail(newService.id), context.previousService);
            }
        },
        onSettled: (_, __, variables) => {
            // Always refetch after error or success
            queryClient.invalidateQueries({ queryKey: queryKeys.services.all });
            queryClient.invalidateQueries({ queryKey: queryKeys.services.detail(variables.id) });
        },
    });
};

export const useAddChildService = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ serviceId, childServiceId }: { serviceId: number | string, childServiceId: number | string }) => api.services.addChildService(serviceId, childServiceId),
        onSuccess: (_, variables) => {
            queryClient.invalidateQueries({ queryKey: queryKeys.services.detail(variables.serviceId) });
            queryClient.invalidateQueries({ queryKey: queryKeys.services.lists() });
        },
    });
};

export const useRemoveChildService = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ serviceId, childServiceId }: { serviceId: number | string, childServiceId: number | string }) => api.services.removeChildService(serviceId, childServiceId),
        onSuccess: (_, variables) => {
            queryClient.invalidateQueries({ queryKey: queryKeys.services.detail(variables.serviceId) });
            queryClient.invalidateQueries({ queryKey: queryKeys.services.lists() });
        },
    });
};

export const useDeleteService = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (id: string | number) => api.services.delete(id),
        onSuccess: (_, deletedId) => {
            queryClient.invalidateQueries({ queryKey: queryKeys.services.all });
            queryClient.removeQueries({ queryKey: queryKeys.services.detail(deletedId) });
        },
    });
};

export const useResetServiceRank = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: () => api.services.resetRank(),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: queryKeys.services.all });
        },
    });
};

export const useMoveCategoryRankUp = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (id: string | number) => api.services.moveCategoryRankUp(id),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: queryKeys.serviceCategories.all });
            queryClient.invalidateQueries({ queryKey: queryKeys.services.all });
        },
    });
};

export const useMoveCategoryRankDown = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (id: string | number) => api.services.moveCategoryRankDown(id),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: queryKeys.serviceCategories.all });
            queryClient.invalidateQueries({ queryKey: queryKeys.services.all });
        },
    });
};

export const useResetCategoryRank = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: () => api.services.resetCategoryRank(),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: queryKeys.serviceCategories.all });
            queryClient.invalidateQueries({ queryKey: queryKeys.services.all });
        },
    });
};

// Service Sell Costs Mutations
export const useCreateServiceSellCost = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (priceData: Partial<ServiceSellCost>) => api.serviceSellCosts.save(priceData),
        onSuccess: (_, variables) => {
            // Invalidate all service sell prices queries
            queryClient.invalidateQueries({ queryKey: queryKeys.serviceSellCosts.all });

            // Invalidate all services queries to refresh the services list
            queryClient.invalidateQueries({ queryKey: queryKeys.services.all });

            // Invalidate the specific service detail to refresh sell_prices array
            if (variables.service_id) {
                queryClient.invalidateQueries({ queryKey: queryKeys.services.detail(variables.service_id) });
            }
        },
    });
};

export const useUpdateServiceSellCost = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (priceData: Partial<ServiceSellCost>) => api.serviceSellCosts.save(priceData),
        onSuccess: (_, variables) => {
            // Invalidate all service sell prices queries
            queryClient.invalidateQueries({ queryKey: queryKeys.serviceSellCosts.all });

            if (variables.id) {
                queryClient.invalidateQueries({ queryKey: queryKeys.serviceSellCosts.detail(variables.id) });
            }

            // Invalidate all services queries to refresh the services list
            queryClient.invalidateQueries({ queryKey: queryKeys.services.all });

            // Invalidate the specific service detail to refresh sell_prices array
            if (variables.service_id) {
                queryClient.invalidateQueries({ queryKey: queryKeys.services.detail(variables.service_id) });
            }
        },
    });
};

export const useDeleteServiceSellCost = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (id: string | number) => api.serviceSellCosts.delete(id),
        onSuccess: (_, deletedId) => {
            queryClient.invalidateQueries({ queryKey: queryKeys.serviceSellCosts.all });
            queryClient.removeQueries({ queryKey: queryKeys.serviceSellCosts.detail(deletedId) });
            // Invalidate all services to refresh sell_prices arrays
            queryClient.invalidateQueries({ queryKey: queryKeys.services.all });
        },
    });
};


// Service Sell Price Mutations
export const useCreateServiceSellPrice = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (priceData: Partial<ServiceSellPrice> & { apply_to_linked_service?: boolean }) => api.serviceSellPrices.save(priceData),
        onSuccess: (_, variables) => {
            // Invalidate all service sell prices queries
            queryClient.invalidateQueries({ queryKey: queryKeys.serviceSellPrices.all });

            // Invalidate all services queries to refresh the services list
            queryClient.invalidateQueries({ queryKey: queryKeys.services.all });

            // Invalidate the specific service detail to refresh sell_prices array
            if (variables.service_id) {
                queryClient.invalidateQueries({ queryKey: queryKeys.services.detail(variables.service_id) });
            }
        },
    });
};

export const useUpdateServiceSellPrice = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (priceData: Partial<ServiceSellPrice> & { apply_to_linked_service?: boolean }) => api.serviceSellPrices.save(priceData),
        onSuccess: (_, variables) => {
            // Invalidate all service sell prices queries
            queryClient.invalidateQueries({ queryKey: queryKeys.serviceSellPrices.all });

            if (variables.id) {
                queryClient.invalidateQueries({ queryKey: queryKeys.serviceSellPrices.detail(variables.id) });
            }

            // Invalidate all services queries to refresh the services list
            queryClient.invalidateQueries({ queryKey: queryKeys.services.all });

            // Invalidate the specific service detail to refresh sell_prices array
            if (variables.service_id) {
                queryClient.invalidateQueries({ queryKey: queryKeys.services.detail(variables.service_id) });
            }
        },
    });
};

export const useDeleteServiceSellPrice = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (id: string | number) => api.serviceSellPrices.delete(id),
        onSuccess: (_, deletedId) => {
            queryClient.invalidateQueries({ queryKey: queryKeys.serviceSellPrices.all });
            queryClient.removeQueries({ queryKey: queryKeys.serviceSellPrices.detail(deletedId) });
            // Invalidate all services to refresh sell_prices arrays
            queryClient.invalidateQueries({ queryKey: queryKeys.services.all });
        },
    });
};

// Service Group Mutations
export const useCreateServiceGroup = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (serviceGroupData: Partial<ServiceGroup>) => api.serviceGroups.save(serviceGroupData),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: queryKeys.serviceGroups.all });
        },
    });
};

// Product Mutations
export const useCreateProduct = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (productData: Partial<Product> & { service_ids?: number[] }) => api.products.save(productData),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: queryKeys.products.all });
            queryClient.invalidateQueries({ queryKey: queryKeys.services.all });
        },
    });
};

export const useUpdateProduct = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (productData: Partial<Product> & { id: number; service_ids?: number[] }) => api.products.save(productData),
        onSuccess: (_, variables) => {
            queryClient.invalidateQueries({ queryKey: queryKeys.products.all });
            queryClient.invalidateQueries({ queryKey: queryKeys.products.detail(variables.id) });
            queryClient.invalidateQueries({ queryKey: queryKeys.services.all });
        },
    });
};

export const useDeleteProduct = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (id: string | number) => api.products.delete(id),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: queryKeys.products.all });
            queryClient.invalidateQueries({ queryKey: queryKeys.services.all });
        },
    });
};

export const useUpdateServiceGroup = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (serviceGroupData: ServiceGroup) => api.serviceGroups.save(serviceGroupData),
        onSuccess: (_, variables) => {
            queryClient.invalidateQueries({ queryKey: queryKeys.serviceGroups.all });
            queryClient.invalidateQueries({ queryKey: queryKeys.serviceGroups.detail(variables.id) });
        },
    });
};

export const useDeleteServiceGroup = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (id: string | number) => api.serviceGroups.delete(id),
        onSuccess: (_, deletedId) => {
            queryClient.invalidateQueries({ queryKey: queryKeys.serviceGroups.all });
            queryClient.removeQueries({ queryKey: queryKeys.serviceGroups.detail(deletedId) });
        },
    });
};

// Service Provider Mutations
export const useCreateServiceProvider = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (serviceProviderData: Partial<ServiceProvider>) => api.serviceProviders.save(serviceProviderData),
        onSuccess: (_, variables) => {
            queryClient.invalidateQueries({ queryKey: queryKeys.serviceProviders.all });
            // Invalidate the specific service to refresh providers array
            if (variables.service_id) {
                queryClient.invalidateQueries({ queryKey: queryKeys.services.detail(variables.service_id) });
            }
            // Invalidate all services list as well
            queryClient.invalidateQueries({ queryKey: queryKeys.services.all });
        },
    });
};

export const useUpdateServiceProvider = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (serviceProviderData: Partial<ServiceProvider> & { id: number }) => api.serviceProviders.save(serviceProviderData),
        onSuccess: (_, variables) => {
            queryClient.invalidateQueries({ queryKey: queryKeys.serviceProviders.all });
            queryClient.invalidateQueries({ queryKey: queryKeys.serviceProviders.detail(variables.id) });
        },
    });
};

export const useDeleteServiceProvider = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (id: string | number) => api.serviceProviders.delete(id),
        onSuccess: (_, deletedId) => {
            queryClient.invalidateQueries({ queryKey: queryKeys.serviceProviders.all });
            queryClient.removeQueries({ queryKey: queryKeys.serviceProviders.detail(deletedId) });
            // Invalidate all services to refresh providers arrays
            queryClient.invalidateQueries({ queryKey: queryKeys.services.all });
        },
    });
};

export const useServiceLinkedServices = (params?: { service_id?: number | string; linked_service_id?: number | string; [key: string]: any }, options?: any) => {
    return useQuery<ApiResponse<ServiceLinkedService[]>, Error>({
        queryKey: queryKeys.serviceLinkedServices.list(params),
        queryFn: () => api.serviceLinkedServices.getAll(params),
        ...options,
    });
};

export const useCreateServiceLinkedService = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (data: Partial<ServiceLinkedService>) => api.serviceLinkedServices.save(data),
        onSuccess: (_, variables) => {
            queryClient.invalidateQueries({ queryKey: queryKeys.serviceLinkedServices.all });
            if (variables.service_id) {
                queryClient.invalidateQueries({ queryKey: queryKeys.services.detail(variables.service_id) });
            }
            if (variables.linked_service_id) {
                queryClient.invalidateQueries({ queryKey: queryKeys.services.detail(variables.linked_service_id) });
            }
            queryClient.invalidateQueries({ queryKey: queryKeys.services.all });
        },
    });
};

export const useDeleteServiceLinkedService = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (id: string | number) => api.serviceLinkedServices.delete(id),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: queryKeys.serviceLinkedServices.all });
            queryClient.invalidateQueries({ queryKey: queryKeys.services.all });
        },
    });
};

export const useAddServiceToGroup = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ groupId, serviceId, discount = 0 }: { groupId: number | string; serviceId: number | string; discount?: number }) =>
            api.serviceGroups.addServiceToGroup(groupId, serviceId, discount),
        onSuccess: (_, { groupId }) => {
            queryClient.invalidateQueries({ queryKey: queryKeys.serviceGroups.detail(groupId) });
        },
    });
};

export const useUpdateServiceGroupLinkDiscount = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ linkId, discount, groupId }: { linkId: number | string; discount: number; groupId: number | string }) =>
            api.serviceGroups.updateServiceLinkDiscount(linkId, discount),
        onSuccess: (_, { groupId }) => {
            queryClient.invalidateQueries({ queryKey: queryKeys.serviceGroups.detail(groupId) });
        },
    });
};

export const useRemoveServiceFromGroup = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ groupId, serviceId }: { groupId: number | string; serviceId: number | string }) =>
            api.serviceGroups.removeServiceFromGroup(groupId, serviceId),
        onSuccess: (_, { groupId }) => {
            queryClient.invalidateQueries({ queryKey: queryKeys.serviceGroups.detail(groupId) });
        },
    });
};

// Service Booking Mutations
export const useSaveServiceBooking = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (bookingData: Partial<ServiceBooking>) => api.serviceBookings.save(bookingData),
        onSuccess: (_, variables) => {
            queryClient.invalidateQueries({ queryKey: queryKeys.serviceBookings.all });
            queryClient.invalidateQueries({ queryKey: queryKeys.reservations.all });
            queryClient.invalidateQueries({ queryKey: queryKeys.serviceOrders.all });
            // Invalidate service bookings for the specific reservation
            if (variables.reservation_id) {
                queryClient.invalidateQueries({ queryKey: queryKeys.services.bookingsList({ reservation_id: variables.reservation_id }) });
            }
        },
    });
};

export const useSaveServiceOrderBooking = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (bookingData: Partial<ServiceBooking>) => api.serviceBookings.saveServiceOrder(bookingData),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: queryKeys.serviceBookings.all });
            queryClient.invalidateQueries({ queryKey: queryKeys.reservations.all });
            queryClient.invalidateQueries({ queryKey: queryKeys.serviceOrders.all });
        },
    });
};

export const useUpdateServiceBooking = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (bookingData: Partial<ServiceBooking>) => api.reservations.saveServiceBooking(bookingData),
        onSuccess: (_, variables) => {
            queryClient.invalidateQueries({ queryKey: queryKeys.serviceBookings.all });
            queryClient.invalidateQueries({ queryKey: queryKeys.serviceBookings.detail(variables.id) });
            queryClient.invalidateQueries({ queryKey: queryKeys.reservations.all });
            // Invalidate service orders data for service-orders-page
            queryClient.invalidateQueries({ queryKey: queryKeys.serviceOrders.all });
            // Invalidate service bookings for the specific reservation
            if (variables.reservation_id) {
                queryClient.invalidateQueries({ queryKey: queryKeys.services.bookingsList({ reservation_id: variables.reservation_id }) });
            }
        },
    });
};

export const useUpdateServiceBookingMoneyIn = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ id, data }: { id: string | number; data: Partial<ServiceBooking> }) =>
            api.serviceBookings.updateMoney(id, data),
        onSuccess: (_, variables) => {
            queryClient.invalidateQueries({ queryKey: queryKeys.serviceBookings.all });
            queryClient.invalidateQueries({ queryKey: queryKeys.serviceBookings.detail(variables.id) });
            queryClient.invalidateQueries({ queryKey: queryKeys.reservations.all });
            queryClient.invalidateQueries({ queryKey: queryKeys.serviceOrders.all });
            if (variables.data.reservation_id) {
                queryClient.invalidateQueries({ queryKey: queryKeys.services.bookingsList({ reservation_id: variables.data.reservation_id }) });
            }
        },
    });
};

export const useResetServiceBookingPrice = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ id }: { id: string | number }) =>
            api.serviceBookings.resetPrice(id),
        onSuccess: (_, variables) => {
            queryClient.invalidateQueries({ queryKey: queryKeys.serviceBookings.all });
            queryClient.invalidateQueries({ queryKey: queryKeys.serviceBookings.detail(variables.id) });
            queryClient.invalidateQueries({ queryKey: queryKeys.reservations.all });
            queryClient.invalidateQueries({ queryKey: queryKeys.serviceOrders.all });
        },
    });
};

export const useResetServiceBookingCost = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ id }: { id: string | number }) =>
            api.serviceBookings.resetCost(id),
        onSuccess: (_, variables) => {
            queryClient.invalidateQueries({ queryKey: queryKeys.serviceBookings.all });
            queryClient.invalidateQueries({ queryKey: queryKeys.serviceBookings.detail(variables.id) });
            queryClient.invalidateQueries({ queryKey: queryKeys.reservations.all });
            queryClient.invalidateQueries({ queryKey: queryKeys.serviceOrders.all });
        },
    });
};

export const useUpdateServiceBookingModifiers = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ id, applied_money_mods, applied_money_out_mods }: { id: string | number; applied_money_mods?: any[]; applied_money_out_mods?: any[] }) =>
            api.serviceBookings.updateModifiers(id, { applied_money_mods, applied_money_out_mods }),
        onSuccess: (_, variables) => {
            queryClient.invalidateQueries({ queryKey: queryKeys.serviceBookings.all });
            queryClient.invalidateQueries({ queryKey: queryKeys.serviceBookings.detail(variables.id) });
            queryClient.invalidateQueries({ queryKey: queryKeys.reservations.all });
            queryClient.invalidateQueries({ queryKey: queryKeys.serviceOrders.all });
        },
    });
};

export const useUpdateServiceBookingServiceData = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ id }: { id: string | number }) =>
            api.serviceBookings.updateServiceData(id),
        onSuccess: (_, variables) => {
            queryClient.invalidateQueries({ queryKey: queryKeys.serviceBookings.all });
            queryClient.invalidateQueries({ queryKey: queryKeys.serviceBookings.detail(variables.id) });
            queryClient.invalidateQueries({ queryKey: queryKeys.reservations.all });
            queryClient.invalidateQueries({ queryKey: queryKeys.serviceOrders.all });
        },
    });
};

export const useUpdateServiceBookingSelfAttr = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ id, key, value }: { id: string | number; key: string; value: any }) =>
            api.reservations.updateSelfAttr(id, key, value),
        onSuccess: (_, variables) => {
            queryClient.invalidateQueries({ queryKey: queryKeys.serviceBookings.all });
            queryClient.invalidateQueries({ queryKey: queryKeys.serviceBookings.detail(variables.id) });
            queryClient.invalidateQueries({ queryKey: queryKeys.reservations.all });
        },
    });
};

export const useUpdateServiceSelfAttr = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ id, key, value }: { id: string | number; key: string; value: any }) =>
            api.reservations.updateSelfAttr(id, key, value),
        onSuccess: (_, variables) => {
            queryClient.invalidateQueries({ queryKey: queryKeys.serviceBookings.all });
            queryClient.invalidateQueries({ queryKey: queryKeys.serviceBookings.detail(variables.id) });
            queryClient.invalidateQueries({ queryKey: queryKeys.reservations.all });
        },
    });
};


export const useSetServiceBookingInventoryItems = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ bookingId, itemIds }: { bookingId: number | string, itemIds: (number | string)[] }) =>
            api.serviceBookings.setInventoryItems(bookingId, itemIds),
        onSuccess: (_, variables) => {
            queryClient.invalidateQueries({ queryKey: queryKeys.serviceBookings.all });
            queryClient.invalidateQueries({ queryKey: queryKeys.serviceBookings.detail(variables.bookingId) });
            // Invalidate reservations as they might show booking status
            queryClient.invalidateQueries({ queryKey: queryKeys.reservations.all });
        },
    });
};

export const useDeleteServiceBooking = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ id, reservation_id }: { id: string | number; reservation_id?: string | number }) =>
            api.reservations.deleteServiceBooking(id),
        onSuccess: (_, { id: deletedId, reservation_id }) => {
            queryClient.invalidateQueries({ queryKey: queryKeys.serviceBookings.all });
            queryClient.removeQueries({ queryKey: queryKeys.serviceBookings.detail(deletedId) });
            queryClient.invalidateQueries({ queryKey: queryKeys.reservations.all });
            queryClient.invalidateQueries({ queryKey: queryKeys.serviceOrders.all });
            // Invalidate service bookings for the specific reservation
            if (reservation_id) {
                queryClient.invalidateQueries({ queryKey: queryKeys.services.bookingsList({ reservation_id }) });
            }
        },
    });
};

// Service Order Mutations
export const useCreateServiceOrder = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (orderData: Partial<ServiceOrder>) => api.serviceOrders.saveServiceOrder(orderData),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: queryKeys.serviceOrders.all });
            queryClient.invalidateQueries({ queryKey: queryKeys.inventoryItems.all });
        },
    });
};

export const useUpdateServiceOrder = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (orderData: Partial<ServiceOrder>) => api.serviceOrders.saveServiceOrder(orderData),
        onSuccess: (_, variables) => {
            queryClient.invalidateQueries({ queryKey: queryKeys.serviceOrders.all });
            queryClient.invalidateQueries({ queryKey: queryKeys.serviceOrders.detail(variables.id) });
            queryClient.invalidateQueries({ queryKey: queryKeys.serviceBookings.all });
        },
    });
};

export const useUpdateServiceOrderBooking = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ id, data }: { id: number | string, data: any }) => api.serviceOrders.updateServiceOrderBooking(id, data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: queryKeys.serviceOrders.all });
            queryClient.invalidateQueries({ queryKey: queryKeys.inventoryItems.all });
        },
    });
};

export const useDeleteServiceOrder = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (id: string | number) => api.serviceOrders.deleteServiceOrder(id),
        onSuccess: (_, deletedId) => {
            queryClient.invalidateQueries({ queryKey: queryKeys.serviceOrders.all });
            queryClient.removeQueries({ queryKey: queryKeys.serviceOrders.detail(deletedId) });
        },
    });
};


export const useAddBookingToOrder = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ serviceOrderId, serviceBookingId }: { serviceOrderId: string | number; serviceBookingId: string | number }) =>
            api.serviceOrders.addBookingToOrder(serviceOrderId, serviceBookingId),
        onSuccess: (_, { serviceOrderId, serviceBookingId }) => {
            queryClient.invalidateQueries({ queryKey: queryKeys.serviceOrders.detail(serviceOrderId) });
            queryClient.invalidateQueries({ queryKey: queryKeys.serviceBookings.detail(serviceBookingId) });
            // Invalidate list queries used by service-orders-page
            queryClient.invalidateQueries({ queryKey: queryKeys.serviceOrders.all });
            queryClient.invalidateQueries({ queryKey: queryKeys.serviceBookings.all });
            // Also invalidate services bookings queries (these might be different)
            queryClient.invalidateQueries({ queryKey: queryKeys.services.bookings() });
            // Invalidate reservations queries to refresh reservation-edit-page
            queryClient.invalidateQueries({ queryKey: queryKeys.reservations.all });
        },
    });
};
export const useRemoveBookingFromOrder = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ serviceOrderId, serviceBookingId }: { serviceOrderId: string | number; serviceBookingId: string | number }) =>
            api.serviceOrders.removeBookingFromOrder(serviceOrderId, serviceBookingId),
        onSuccess: (_, { serviceOrderId, serviceBookingId }) => {
            queryClient.invalidateQueries({ queryKey: queryKeys.serviceOrders.detail(serviceOrderId) });
            queryClient.invalidateQueries({ queryKey: queryKeys.serviceBookings.detail(serviceBookingId) });
            // Invalidate list queries used by service-orders-page
            queryClient.invalidateQueries({ queryKey: queryKeys.serviceOrders.all });
            queryClient.invalidateQueries({ queryKey: queryKeys.serviceBookings.all });
            // Also invalidate services bookings queries (these might be different)
            queryClient.invalidateQueries({ queryKey: queryKeys.services.bookings() });
            // Invalidate reservations queries to refresh reservation-edit-page
            queryClient.invalidateQueries({ queryKey: queryKeys.reservations.all });
        },
    });
};

export const useAddServiceOrderInventoryItem = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ serviceOrderId, inventoryItemId }: { serviceOrderId: number | string, inventoryItemId: number | string }) =>
            api.serviceOrders.linkServiceOrderToInventoryItem(serviceOrderId, inventoryItemId),
        onSuccess: (_, variables) => {
            queryClient.invalidateQueries({ queryKey: queryKeys.serviceOrders.detail(variables.serviceOrderId) });
            queryClient.invalidateQueries({ queryKey: queryKeys.serviceOrders.all });

        },
    });
};

export const useRemoveServiceOrderInventoryItem = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ serviceOrderId, linkId }: { serviceOrderId: number | string, linkId: number | string }) =>
            api.serviceOrders.unlinkServiceOrderToInventoryItem(linkId),
        onSuccess: (_, variables) => {
            queryClient.invalidateQueries({ queryKey: queryKeys.serviceOrders.detail(variables.serviceOrderId) });
            queryClient.invalidateQueries({ queryKey: queryKeys.serviceOrders.all });

        },
    });
};

// Person Mutations
export const useCreatePerson = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (personData: Partial<Person>) => api.persons.save(personData),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: queryKeys.persons.all });
            queryClient.invalidateQueries({ queryKey: queryKeys.persons.staff() });
        },
    });
};

export const useUpdatePerson = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (personData: Partial<Person>) => api.persons.save(personData),
        onSuccess: (_, variables) => {
            queryClient.invalidateQueries({ queryKey: queryKeys.persons.all });
            queryClient.invalidateQueries({ queryKey: queryKeys.persons.detail(variables.id) });
            queryClient.invalidateQueries({ queryKey: queryKeys.persons.staff() });
            queryClient.invalidateQueries({ queryKey: queryKeys.businesses.all });
            // Also invalidate reservations since person data is embedded
            queryClient.invalidateQueries({ queryKey: queryKeys.reservations.all });
        },
    });
};

export const useDeletePerson = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (id: string | number) => api.persons.delete(id),
        onSuccess: (_, deletedId) => {
            queryClient.invalidateQueries({ queryKey: queryKeys.persons.all });
            queryClient.removeQueries({ queryKey: queryKeys.persons.detail(deletedId) });
            queryClient.invalidateQueries({ queryKey: queryKeys.persons.staff() });
            queryClient.invalidateQueries({ queryKey: queryKeys.reservations.all });
        },
    });
};

// Person Reservation Association Mutations
export const useAddPersonToReservation = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ reservationId, personId }: { reservationId: number; personId: number }) =>
            api.persons.addToReservation(reservationId, personId),
        onSuccess: (_, { reservationId, personId }) => {
            queryClient.invalidateQueries({ queryKey: queryKeys.reservations.detail(reservationId) });
            queryClient.invalidateQueries({ queryKey: queryKeys.persons.detail(personId) });
        },
    });
};

export const useRemovePersonFromReservation = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ reservationId, personId }: { reservationId: number; personId: number }) =>
            api.persons.removeFromReservation(reservationId, personId),
        onSuccess: (_, { reservationId, personId }) => {
            queryClient.invalidateQueries({ queryKey: queryKeys.reservations.detail(reservationId) });
            queryClient.invalidateQueries({ queryKey: queryKeys.persons.detail(personId) });
        },
    });
};

export const useSplitReservationPax = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ reservationId, paxId, newPaxs }: { reservationId: number; paxId: number; newPaxs: any[] }) =>
            api.reservations.splitPax(reservationId, paxId, newPaxs),
        onSuccess: (_, { reservationId }) => {
            queryClient.invalidateQueries({ queryKey: queryKeys.reservations.detail(reservationId) });
            queryClient.invalidateQueries({ queryKey: queryKeys.serviceBookings.all });
            queryClient.invalidateQueries({ queryKey: queryKeys.services.bookingsList({ reservation_id: reservationId }) });
        },
    });
};

export const useReservationPaxGroups = (reservationId: number | string) => {
    return useQuery({
        queryKey: queryKeys.reservations.paxGroups(reservationId),
        queryFn: async () => {
            const res = await api.paxGroups.getByReservation(reservationId);
            return (res.data || []) as ReservationPaxGroup[];
        },
        enabled: !!reservationId,
    });
};

export const useCreateReservationPaxGroup = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ reservationId, data }: { reservationId: number | string; data: Partial<ReservationPaxGroup> }) =>
            api.paxGroups.create(reservationId, data),
        onSuccess: (_, { reservationId }) => {
            queryClient.invalidateQueries({ queryKey: queryKeys.reservations.detail(reservationId) });
            queryClient.invalidateQueries({ queryKey: queryKeys.reservations.paxGroups(reservationId) });
            queryClient.invalidateQueries({ queryKey: queryKeys.serviceBookings.all });
            queryClient.invalidateQueries({ queryKey: queryKeys.serviceOrders.all });
        },
    });
};

export const useUpdateReservationPaxGroup = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ reservationId, groupId, data }: { reservationId: number | string; groupId: number | string; data: Partial<ReservationPaxGroup> }) =>
            api.paxGroups.update(reservationId, groupId, data),
        onSuccess: (_, { reservationId }) => {
            queryClient.invalidateQueries({ queryKey: queryKeys.reservations.detail(reservationId) });
            queryClient.invalidateQueries({ queryKey: queryKeys.reservations.paxGroups(reservationId) });
            queryClient.invalidateQueries({ queryKey: queryKeys.serviceBookings.all });
            queryClient.invalidateQueries({ queryKey: queryKeys.serviceOrders.all });
        },
    });
};

export const useDeleteReservationPaxGroup = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ reservationId, groupId }: { reservationId: number | string; groupId: number | string }) =>
            api.paxGroups.delete(reservationId, groupId),
        onSuccess: (_, { reservationId }) => {
            queryClient.invalidateQueries({ queryKey: queryKeys.reservations.detail(reservationId) });
            queryClient.invalidateQueries({ queryKey: queryKeys.reservations.paxGroups(reservationId) });
            queryClient.invalidateQueries({ queryKey: queryKeys.serviceBookings.all });
            queryClient.invalidateQueries({ queryKey: queryKeys.serviceOrders.all });
        },
    });
};

export const useAddPaxGroupToAllBookings = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ reservationId, groupId }: { reservationId: number | string; groupId: number | string }) =>
            api.paxGroups.addToAllBookings(reservationId, groupId),
        onSuccess: (_, { reservationId }) => {
            queryClient.invalidateQueries({ queryKey: queryKeys.reservations.detail(reservationId) });
            queryClient.invalidateQueries({ queryKey: queryKeys.reservations.paxGroups(reservationId) });
            queryClient.invalidateQueries({ queryKey: queryKeys.serviceBookings.all });
            queryClient.invalidateQueries({ queryKey: queryKeys.serviceOrders.all });
        },
    });
};

export const useRemovePaxGroupFromAllBookings = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ reservationId, groupId }: { reservationId: number | string; groupId: number | string }) =>
            api.paxGroups.removeFromAllBookings(reservationId, groupId),
        onSuccess: (_, { reservationId }) => {
            queryClient.invalidateQueries({ queryKey: queryKeys.reservations.detail(reservationId) });
            queryClient.invalidateQueries({ queryKey: queryKeys.reservations.paxGroups(reservationId) });
            queryClient.invalidateQueries({ queryKey: queryKeys.serviceBookings.all });
            queryClient.invalidateQueries({ queryKey: queryKeys.serviceOrders.all });
        },
    });
};

export const useAssignPaxGroupToServiceBooking = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ serviceBookingId, groupId, data, reservationId }: { serviceBookingId: number | string; groupId: number | string; data?: Partial<ServiceBookingPaxGroup>; reservationId?: number | string }) =>
            api.paxGroups.assignToServiceBooking(serviceBookingId, groupId, data),
        onSuccess: (_, { serviceBookingId, reservationId }) => {
            if (reservationId) {
                queryClient.invalidateQueries({ queryKey: queryKeys.reservations.detail(reservationId) });
                queryClient.invalidateQueries({ queryKey: queryKeys.reservations.paxGroups(reservationId) });
            }
            queryClient.invalidateQueries({ queryKey: queryKeys.serviceBookings.detail(serviceBookingId) });
            queryClient.invalidateQueries({ queryKey: queryKeys.serviceBookings.all });
            queryClient.invalidateQueries({ queryKey: queryKeys.serviceOrders.all });
        },
    });
};

export const useRemovePaxGroupFromServiceBooking = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ serviceBookingId, groupId, reservationId }: { serviceBookingId: number | string; groupId: number | string; reservationId?: number | string }) =>
            api.paxGroups.removeFromServiceBooking(serviceBookingId, groupId),
        onSuccess: (_, { serviceBookingId, reservationId }) => {
            if (reservationId) {
                queryClient.invalidateQueries({ queryKey: queryKeys.reservations.detail(reservationId) });
                queryClient.invalidateQueries({ queryKey: queryKeys.reservations.paxGroups(reservationId) });
            }
            queryClient.invalidateQueries({ queryKey: queryKeys.serviceBookings.detail(serviceBookingId) });
            queryClient.invalidateQueries({ queryKey: queryKeys.serviceBookings.all });
            queryClient.invalidateQueries({ queryKey: queryKeys.serviceOrders.all });
        },
    });
};

export const useAddPriceModifier = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ reservationId, data }: { reservationId: number | string; data: any }) =>
            api.reservations.addPriceModifier(reservationId, data),
        onSuccess: (_, { reservationId }) => {
            queryClient.invalidateQueries({ queryKey: queryKeys.reservations.detail(reservationId) });
        },
    });
};

export const useRemovePriceModifier = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ id, reservationId }: { id: number | string; reservationId: number | string }) =>
            api.reservations.deletePriceModifier(id),
        onSuccess: (_, { reservationId }) => {
            queryClient.invalidateQueries({ queryKey: queryKeys.reservations.detail(reservationId) });
        },
    });
};

// Person Service Booking Association Mutations
export const useAddPersonToServiceBooking = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({
            serviceBookingId,
            personId,
            paxCount,
            adults,
            children,
            infants,
            seniors,
            guests
        }: {
            serviceBookingId: number;
            personId: number;
            paxCount?: number;
            adults?: number;
            children?: number;
            infants?: number;
            seniors?: number;
            guests?: number;
        }) => {
            const details = (adults !== undefined || children !== undefined || infants !== undefined || seniors !== undefined || guests !== undefined)
                ? { adults, children, infants, seniors, guests }
                : paxCount;
            return api.persons.addToServiceBooking(serviceBookingId, personId, details);
        },
        onSuccess: (_, { serviceBookingId, personId }) => {
            queryClient.invalidateQueries({ queryKey: queryKeys.serviceBookings.all });
            queryClient.invalidateQueries({ queryKey: queryKeys.serviceBookings.detail(serviceBookingId) });
            queryClient.invalidateQueries({ queryKey: queryKeys.persons.detail(personId) });
            queryClient.invalidateQueries({ queryKey: queryKeys.reservations.all });
            queryClient.invalidateQueries({ queryKey: queryKeys.serviceOrders.all });
        },
    });
};

export const useAddPersonToAllBookings = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ reservationId, personId, serviceBookingIds, paxCount }: { reservationId: number; personId: number; serviceBookingIds: number[]; paxCount?: number }) => {
            const promises = serviceBookingIds.map(bookingId =>
                api.persons.addToServiceBooking(bookingId, personId, paxCount)
            );
            return Promise.all(promises);
        },
        onSuccess: (_, { reservationId, personId, serviceBookingIds }) => {
            queryClient.invalidateQueries({ queryKey: queryKeys.serviceBookings.all });
            serviceBookingIds.forEach(id => {
                queryClient.invalidateQueries({ queryKey: queryKeys.serviceBookings.detail(id) });
            });
            queryClient.invalidateQueries({ queryKey: queryKeys.persons.detail(personId) });
            queryClient.invalidateQueries({ queryKey: queryKeys.reservations.all });
            queryClient.invalidateQueries({ queryKey: queryKeys.reservations.detail(reservationId) });
            queryClient.invalidateQueries({ queryKey: queryKeys.serviceOrders.all });
        },
    });
};

export const useRemovePersonFromAllBookings = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ reservationId, personId, serviceBookingIds }: { reservationId: number; personId: number; serviceBookingIds: number[] }) => {
            const promises = serviceBookingIds.map(bookingId =>
                api.persons.removeFromServiceBooking(bookingId, personId)
            );
            return Promise.all(promises);
        },
        onSuccess: (_, { reservationId, personId, serviceBookingIds }) => {
            queryClient.invalidateQueries({ queryKey: queryKeys.serviceBookings.all });
            serviceBookingIds.forEach(id => {
                queryClient.invalidateQueries({ queryKey: queryKeys.serviceBookings.detail(id) });
            });
            queryClient.invalidateQueries({ queryKey: queryKeys.persons.detail(personId) });
            queryClient.invalidateQueries({ queryKey: queryKeys.reservations.all });
            queryClient.invalidateQueries({ queryKey: queryKeys.reservations.detail(reservationId) });
            queryClient.invalidateQueries({ queryKey: queryKeys.serviceOrders.all });
        },
    });
};

export const useRemovePersonFromServiceBooking = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ serviceBookingId, personId }: { serviceBookingId: number; personId: number }) =>
            api.persons.removeFromServiceBooking(serviceBookingId, personId),
        onSuccess: (_, { serviceBookingId, personId }) => {
            queryClient.invalidateQueries({ queryKey: queryKeys.serviceBookings.all });
            queryClient.invalidateQueries({ queryKey: queryKeys.serviceBookings.detail(serviceBookingId) });
            queryClient.invalidateQueries({ queryKey: queryKeys.persons.detail(personId) });
            queryClient.invalidateQueries({ queryKey: queryKeys.reservations.all });
            queryClient.invalidateQueries({ queryKey: queryKeys.serviceOrders.all });
        },
    });
};

// Person Service Order Association Mutations
export const useAddPersonToServiceOrder = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ serviceOrderId, personId }: { serviceOrderId: number; personId: number }) =>
            api.persons.addToServiceOrder(serviceOrderId, personId),
        onSuccess: (_, { serviceOrderId, personId }) => {
            queryClient.invalidateQueries({ queryKey: queryKeys.serviceOrders.all });
            queryClient.invalidateQueries({ queryKey: queryKeys.serviceOrders.detail(serviceOrderId) });
            queryClient.invalidateQueries({ queryKey: queryKeys.persons.detail(personId) });
        },
    });
};

export const useRemovePersonFromServiceOrder = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ serviceOrderId, personId }: { serviceOrderId: number; personId: number }) =>
            api.persons.removeFromServiceOrder(serviceOrderId, personId),
        onSuccess: (_, { serviceOrderId, personId }) => {
            queryClient.invalidateQueries({ queryKey: queryKeys.serviceOrders.all });
            queryClient.invalidateQueries({ queryKey: queryKeys.serviceOrders.detail(serviceOrderId) });
            queryClient.invalidateQueries({ queryKey: queryKeys.persons.detail(personId) });
        },
    });
};

// Inventory Item Mutations
export const useCreateInventoryItem = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (inventoryItemData: Partial<InventoryItem>) => api.inventoryItems.save(inventoryItemData),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: queryKeys.inventoryItems.all });
        },
    });
};

export const useUpdateInventoryItem = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (inventoryItemData: InventoryItem) => api.inventoryItems.save(inventoryItemData),
        onSuccess: (_, variables) => {
            queryClient.invalidateQueries({ queryKey: queryKeys.inventoryItems.all });
            queryClient.invalidateQueries({ queryKey: queryKeys.inventoryItems.detail(variables.id) });
        },
    });
};

export const useDeleteInventoryItem = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (id: string | number) => api.inventoryItems.delete(id),
        onSuccess: (_, deletedId) => {
            queryClient.invalidateQueries({ queryKey: queryKeys.inventoryItems.all });
            queryClient.removeQueries({ queryKey: queryKeys.inventoryItems.detail(deletedId) });
        },
    });
};



// Tenant Mutations
export const useUpdateTenant = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (tenantData: any) => api.tenants.save(tenantData),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: queryKeys.tenants.all });
        },
    });
};

export const useCreateAfipCert = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (variables: { tenantId?: number | string; data?: any } | any) => {
            const payload = variables?.data !== undefined ? variables.data : variables;
            return api.afip.createCert(payload);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: queryKeys.tenants.all });
            queryClient.invalidateQueries({ queryKey: ['afip'] });
        },
    });
};

export const useAuthAfipService = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (variables: { tenantId?: number | string; data?: any } | any) => {
            const payload = variables?.data !== undefined ? variables.data : variables;
            return api.afip.authService(payload);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: queryKeys.tenants.all });
            queryClient.invalidateQueries({ queryKey: ['afip'] });
        },
    });
};

export const useCreateAfipSalesPoint = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (variables: { tenantId?: number | string; data?: any } | any) => {
            const payload = variables?.data !== undefined ? variables.data : variables;
            return api.afip.createSalesPoint(payload);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: queryKeys.tenants.all });
            queryClient.invalidateQueries({ queryKey: ['afip'] });
        },
    });
};

export const useSyncAfipSalesPoints = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (variables: { tenantId?: number | string; data?: any } | any) => {
            const payload = variables?.data !== undefined ? variables.data : variables;
            return api.afip.syncSalesPoints(payload);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: queryKeys.tenants.all });
            queryClient.invalidateQueries({ queryKey: ['afip'] });
        },
    });
};

export const useGenerateAfipInvoice = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (variables: { tenantId?: number | string; data?: any } | any) => {
            const payload = variables?.data !== undefined ? variables.data : variables;
            return api.afip.generateInvoice(payload);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: queryKeys.tenants.all });
            queryClient.invalidateQueries({ queryKey: ['afip'] });
        },
    });
};

export const useGetAfipVouchers = (
    tenantIdOrParams?: number | string | { direction?: 'emitted' | 'received' | 'all'; issuer_business_id?: number; service_booking_id?: number; reservation_id?: number },
    options = {}
) => {
    const params = typeof tenantIdOrParams === 'object' && tenantIdOrParams !== null ? tenantIdOrParams : undefined;
    return useQuery<AfipVoucher[]>({
        queryKey: ['afip', 'vouchers', tenantIdOrParams],
        queryFn: () => api.afip.getVouchers(params),
        ...options,
    });
};

export const useCreateReceivedAfipVoucher = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (data: any) => api.afip.createReceivedVoucher(data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['afip'] });
            queryClient.invalidateQueries({ queryKey: queryKeys.serviceBookings.all });
            queryClient.invalidateQueries({ queryKey: queryKeys.reservations.all });
        },
    });
};

export const useUpdateReceivedAfipVoucher = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ voucherId, data }: { voucherId: number | string; data: any }) =>
            api.afip.updateReceivedVoucher(voucherId, data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['afip'] });
            queryClient.invalidateQueries({ queryKey: queryKeys.serviceBookings.all });
            queryClient.invalidateQueries({ queryKey: queryKeys.reservations.all });
        },
    });
};

export const useDeleteReceivedAfipVoucher = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (voucherId: number | string) => api.afip.deleteReceivedVoucher(voucherId),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['afip'] });
            queryClient.invalidateQueries({ queryKey: queryKeys.serviceBookings.all });
            queryClient.invalidateQueries({ queryKey: queryKeys.reservations.all });
        },
    });
};

export const useLinkVoucherToServiceBooking = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ serviceBookingId, voucherId, amount }: { serviceBookingId: number | string; voucherId: number | string; amount?: number | null }) =>
            api.afip.linkVoucherToServiceBooking(serviceBookingId, voucherId, amount),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['afip'] });
            queryClient.invalidateQueries({ queryKey: queryKeys.serviceBookings.all });
            queryClient.invalidateQueries({ queryKey: queryKeys.reservations.all });
        },
    });
};

export const useUnlinkVoucherFromServiceBooking = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ serviceBookingId, voucherId }: { serviceBookingId: number | string; voucherId: number | string }) =>
            api.afip.unlinkVoucherFromServiceBooking(serviceBookingId, voucherId),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['afip'] });
            queryClient.invalidateQueries({ queryKey: queryKeys.serviceBookings.all });
            queryClient.invalidateQueries({ queryKey: queryKeys.reservations.all });
        },
    });
};

export const useSendAfipVoucherEmail = () => {
    return useMutation({
        mutationFn: ({ voucherId, sendTo }: { voucherId: number | string; sendTo: string | string[] }) => {
            return api.afip.sendVoucherEmail(voucherId, sendTo);
        },
    });
};

// Actions
export const useExecuteAction = () => {
    return useMutation({
        mutationFn: ({ action, payload }: { action: string; payload: any }) =>
            api.actions.execute(action, payload),
    });
};

// ===============================
// Utility Hooks
// ===============================

// Hook to check if there are any mutations in progress
export const useIsMutating = () => {
    const queryClient = useQueryClient();
    return queryClient.isMutating() > 0;
};

// Hook to check if specific queries are fetching
export const useIsFetchingServices = () => {
    const queryClient = useQueryClient();
    return queryClient.isFetching({ queryKey: queryKeys.services.all }) > 0;
};

export const useIsFetchingReservations = () => {
    const queryClient = useQueryClient();
    return queryClient.isFetching({ queryKey: queryKeys.reservations.all }) > 0;
};

// Prefetch utilities
export const usePrefetchService = () => {
    const queryClient = useQueryClient();

    return (id: string | number) => {
        queryClient.prefetchQuery(serviceQueries.detail(id));
    };
};

export const usePrefetchReservation = () => {
    const queryClient = useQueryClient();

    return (id: string | number) => {
        queryClient.prefetchQuery(reservationQueries.detail(id));
    };
};

// ===============================
// Money Transaction Hooks
// ===============================

export const useMoneyTransactions = (params?: any, options?: any) => {
    return useQuery<ApiResponse<MoneyTransaction[]>>({
        queryKey: queryKeys.moneyTransactions.list(params),
        queryFn: () => api.moneyTransactions.getAll(params),
        staleTime: 30 * 1000,
        ...options,
    });
};

export const useMoneyTransactionsIn = (params?: any, options?: any) => {
    return useQuery<ApiResponse<MoneyTransaction[]>>({
        queryKey: queryKeys.moneyTransactions.listIn(params),
        queryFn: () => api.moneyTransactions.getAllIn(params),
        staleTime: 30 * 1000,
        ...options,
    });
};

export const useMoneyTransactionsOut = (params?: any, options?: any) => {
    return useQuery<ApiResponse<MoneyTransaction[]>>({
        queryKey: queryKeys.moneyTransactions.listOut(params),
        queryFn: () => api.moneyTransactions.getAllOut(params),
        staleTime: 30 * 1000,
        ...options,
    });
};

export const useBusinessBalance = (params?: any, options?: any) => {
    return useQuery<ApiResponse<BusinessBalance[]>>({
        queryKey: queryKeys.businessBalance.list(params),
        queryFn: () => api.businessBalance.getAll(params),
        staleTime: 30 * 1000,
        ...options,
    });
};

export const useClientHistory = (params?: any, options?: any) => {
    return useQuery<ApiResponse<ClientHistory>>({
        queryKey: queryKeys.clientHistory.list(params),
        queryFn: () => api.clientHistory.getAll(params),
        staleTime: 30 * 1000,
        ...options,
    });
};

export const usePersonStatement = (params?: any, options?: any) => {
    return useQuery<ApiResponse<ClientHistory>>({
        queryKey: queryKeys.personStatement.list(params),
        queryFn: () => api.personStatement.getAll(params),
        staleTime: 30 * 1000,
        ...options,
    });
};

export const useMoneyTransaction = (id: number | string) => {
    return useQuery<ApiResponse<MoneyTransaction>>({
        queryKey: queryKeys.moneyTransactions.detail(id),
        queryFn: () => api.moneyTransactions.getOne(id),
        staleTime: 30 * 1000,
        enabled: !!id,
    });
};

export const useCreateMoneyTransaction = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (transactionData: Partial<MoneyTransaction>) => api.moneyTransactions.save(transactionData),
        onSuccess: (_, variables) => {
            queryClient.invalidateQueries({ queryKey: queryKeys.moneyTransactions.all });
        },
    });
};

export const useUpdateMoneyTransaction = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (transactionData: Partial<MoneyTransaction>) => api.moneyTransactions.save(transactionData),
        onSuccess: (_, variables) => {
            queryClient.invalidateQueries({ queryKey: queryKeys.moneyTransactions.all });
            if (variables.id) {
                queryClient.invalidateQueries({ queryKey: queryKeys.moneyTransactions.detail(variables.id) });
            }
            queryClient.invalidateQueries({ queryKey: queryKeys.reservations.all });
        },
    });
};

export const useDeleteMoneyTransaction = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (id: string | number) => api.moneyTransactions.delete(id),
        onSuccess: (_, deletedId) => {
            queryClient.invalidateQueries({ queryKey: queryKeys.moneyTransactions.all });
            queryClient.removeQueries({ queryKey: queryKeys.moneyTransactions.detail(deletedId) });
            queryClient.invalidateQueries({ queryKey: queryKeys.reservations.all });
        },
    });
};

// ===============================
// Exchange Rate Hooks
// ===============================

export const useExchangeRates = (params?: any, options?: any) => {
    return useQuery<ApiResponse<ExchangeRate[]>>({
        queryKey: queryKeys.exchangeRates.list(params),
        queryFn: () => api.exchangeRates.getAll(params),
        staleTime: 5 * 60 * 1000, // 5 minutes
        ...options,
    });
};

export const useExchangeRate = (id: number | string) => {
    return useQuery<ApiResponse<ExchangeRate>>({
        queryKey: queryKeys.exchangeRates.detail(id),
        queryFn: () => api.exchangeRates.getOne(id),
        staleTime: 5 * 60 * 1000,
        enabled: !!id,
    });
};

export const useCreateExchangeRate = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (exchangeRateData: Partial<ExchangeRate>) => api.exchangeRates.save(exchangeRateData),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: queryKeys.exchangeRates.all });
        },
    });
};

export const useUpdateExchangeRate = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (exchangeRateData: Partial<ExchangeRate>) => api.exchangeRates.save(exchangeRateData),
        onSuccess: (_, variables) => {
            queryClient.invalidateQueries({ queryKey: queryKeys.exchangeRates.all });
            if (variables.id) {
                queryClient.invalidateQueries({ queryKey: queryKeys.exchangeRates.detail(variables.id) });
            }
        },
    });
};

export const useDeleteExchangeRate = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (id: string | number) => api.exchangeRates.delete(id),
        onSuccess: (_, deletedId) => {
            queryClient.invalidateQueries({ queryKey: queryKeys.exchangeRates.all });
            queryClient.removeQueries({ queryKey: queryKeys.exchangeRates.detail(deletedId) });
        },
    });
};

// ===============================
// Newsletter Hooks
// ===============================

export const useNewsletterSubscription = () => {
    return useMutation({
        mutationFn: (data: { email: string; extra_data?: Record<string, any> }) =>
            api.newsletter.subscribe(data),
    });
};

export const useNewsletterUnsubscribe = () => {
    return useMutation({
        mutationFn: (email: string) => api.newsletter.unsubscribe(email),
    });
};

// ===============================
// Input Suggestions Hooks
// ===============================

export const useInputSuggestions = (
    params: { type: string; id: string | number; key: string },
    options?: { enabled?: boolean }
) => {
    return useQuery<ApiResponse<string[]>>({
        queryKey: queryKeys.inputSuggestions.get(params),
        queryFn: () => api.inputSuggestions.get(params),
        staleTime: 5 * 60 * 1000, // 5 minutes
        enabled: options?.enabled !== undefined ? options.enabled : !!(params.type && params.id && params.key),
    });
};

// ===============================
// Discount Categories Hooks
// ===============================

export const useDiscountCategories = (params?: any, options?: any) => {
    return useQuery<ApiResponse<DiscountCategory[]>>({
        queryKey: queryKeys.discountCategories.list(params),
        queryFn: () => api.discountCategories.getAll(params),
        staleTime: 5 * 60 * 1000, // 5 minutes
        ...options,
    });
};

export const useDiscountCategory = (id: number | string) => {
    return useQuery<ApiResponse<DiscountCategory>>({
        queryKey: queryKeys.discountCategories.detail(id),
        queryFn: () => api.discountCategories.getOne(id),
        staleTime: 5 * 60 * 1000,
        enabled: !!id,
    });
};

export const useCreateDiscountCategory = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (discountCategoryData: Partial<DiscountCategory>) => api.discountCategories.save(discountCategoryData),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: queryKeys.discountCategories.all });
        },
    });
};

export const useUpdateDiscountCategory = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (discountCategoryData: Partial<DiscountCategory>) => api.discountCategories.save(discountCategoryData),
        onSuccess: (_, variables) => {
            queryClient.invalidateQueries({ queryKey: queryKeys.discountCategories.all });
            if (variables.id) {
                queryClient.invalidateQueries({ queryKey: queryKeys.discountCategories.detail(variables.id) });
            }
        },
    });
};

export const useDeleteDiscountCategory = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (id: string | number) => api.discountCategories.delete(id),
        onSuccess: (_, deletedId) => {
            queryClient.invalidateQueries({ queryKey: queryKeys.discountCategories.all });
            queryClient.removeQueries({ queryKey: queryKeys.discountCategories.detail(deletedId) });
        },
    });
};

// ===============================
// Service Categories Hooks
// ===============================

export const useServiceCategories = (params?: any, options?: any) => {
    return useQuery<ApiResponse<ServiceCategory[]>>({
        queryKey: queryKeys.serviceCategories.list(params),
        queryFn: () => api.services.getCategories(params),
        staleTime: 5 * 60 * 1000, // 5 minutes
        ...options,
    });
};

// ===============================
// System Logs Hooks
// ===============================

export const useSystemLogs = (params?: any, options?: any) => {
    return useQuery<ApiResponse<SystemLog[]>>({
        queryKey: queryKeys.systemLogs.list(params),
        queryFn: () => api.systemLogs.getAll(params),
        staleTime: 30 * 1000, // 30 seconds
        ...options,
    });
};

// ===============================
// Tickets Hooks
// ===============================

export const useTickets = (params?: any, options?: any) => {
    return useQuery<ApiResponse<Ticket[]>>({
        queryKey: queryKeys.tickets.list(params),
        queryFn: () => api.tickets.getAll(params),
        staleTime: 30 * 1000, // 30 seconds
        ...options,
    });
};

export const useTicket = (id?: string | number, options?: any) => {
    return useQuery<ApiResponse<Ticket>>({
        queryKey: queryKeys.tickets.detail(id!),
        queryFn: () => api.tickets.getOne(id!),
        enabled: !!id && id !== 'new',
        staleTime: 30 * 1000, // 30 seconds
        ...options,
    });
};

export const useCreateTicket = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (ticketData: Partial<Ticket>) => api.tickets.save(ticketData),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: queryKeys.tickets.all });
        },
    });
};

export const useCreateTicketComment = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ ticketId, data }: { ticketId: number | string, data: any }) => api.tickets.createComment(ticketId, data),
        onSuccess: (_, variables) => {
            queryClient.invalidateQueries({ queryKey: queryKeys.tickets.detail(String(variables.ticketId)) });
        },
    });
};

export const useUpdateTicket = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (ticketData: Partial<Ticket>) => api.tickets.save(ticketData),
        onSuccess: (_, variables) => {
            queryClient.invalidateQueries({ queryKey: queryKeys.tickets.all });
            if (variables.id) {
                queryClient.invalidateQueries({ queryKey: queryKeys.tickets.detail(variables.id) });
            }
        },
    });
};

export const useDeleteTicket = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (id: string | number) => api.tickets.delete(id),
        onSuccess: (_, deletedId) => {
            queryClient.invalidateQueries({ queryKey: queryKeys.tickets.all });
            queryClient.removeQueries({ queryKey: queryKeys.tickets.detail(deletedId) });
        },
    });
};

// ===============================
// Home Feed Hooks
// ===============================

export interface HomeFeedData {
    pending_tickets: Ticket[];
    last_edited_reservations: Reservation[];
    last_edited_services: Service[];
}

export const useHomeFeed = (options?: any) => {
    return useQuery<ApiResponse<HomeFeedData>>({
        queryKey: queryKeys.homeFeed.get(),
        queryFn: () => api.homeFeed.get(),
        staleTime: 30 * 1000, // 30 seconds
        ...options,
    });
};

export interface SharedWithMeData {
    reservations: Reservation[];
    services: Service[];
    tickets: Ticket[];
    businesses: Business[];
    service_orders?: ServiceOrder[];
}
// Subscriptions
export const subscriptionQueries = {
    all: (params?: any) => queryOptions({
        queryKey: queryKeys.subscriptions.list(params),
        queryFn: () => api.subscriptions.getAll(params),
        staleTime: 60 * 1000,
    }),

    detail: (id: string | number) => queryOptions({
        queryKey: queryKeys.subscriptions.detail(id),
        queryFn: () => api.subscriptions.getOne(id),
        staleTime: 60 * 1000,
        enabled: !!id,
    }),
};

// Subscription Hooks
export const useSubscriptions = (params?: any, options?: any) => useQuery<ApiResponse<Subscription[]>, Error>({
    ...subscriptionQueries.all(params),
    ...options
});
export const useSubscription = (id: string | number) => useQuery<ApiResponse<Subscription>, Error>(subscriptionQueries.detail(id));

export const useCreateSubscription = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (data: Partial<Subscription>) => api.subscriptions.save(data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: queryKeys.subscriptions.all });
            notify.success('operation.success', 'operation.save_subscription.success');
        },
        onError: (error: any) => {
            notify.error('operation.error', 'operation.save_subscription.error');
        }
    });
};

export const useUpdateSubscription = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (data: Partial<Subscription>) => api.subscriptions.save(data),
        onSuccess: (_, variables) => {
            queryClient.invalidateQueries({ queryKey: queryKeys.subscriptions.all });
            if (variables.id) {
                queryClient.invalidateQueries({ queryKey: queryKeys.subscriptions.detail(variables.id) });
            }
            notify.success('operation.success', 'operation.save_subscription.success');
        },
        onError: (error: any) => {
            notify.error('operation.error', 'operation.save_subscription.error');
        }
    });
};

export const useDeleteSubscription = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (id: string | number) => api.subscriptions.delete(id),
        onSuccess: (_, deletedId) => {
            queryClient.invalidateQueries({ queryKey: queryKeys.subscriptions.all });
            queryClient.removeQueries({ queryKey: queryKeys.subscriptions.detail(deletedId) });
        },
    });
};

export const useAddSubscriptionPayment = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ subscriptionId, moneyTransactionId }: { subscriptionId: string | number, moneyTransactionId: string | number }) =>
            api.subscriptions.addPayment(subscriptionId, moneyTransactionId),
        onSuccess: (_, variables) => {
            queryClient.invalidateQueries({ queryKey: queryKeys.subscriptions.detail(variables.subscriptionId) });
            notify.success('operation.success', 'operation.add_payment.success');
        },
        onError: (error: any) => {
            notify.error('operation.error', 'operation.add_payment.error');
        }
    });
};

export const useRemoveSubscriptionPayment = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ subscriptionId, moneyTransactionId }: { subscriptionId: string | number, moneyTransactionId: string | number }) =>
            api.subscriptions.removePayment(subscriptionId, moneyTransactionId),
        onSuccess: (_, variables) => {
            queryClient.invalidateQueries({ queryKey: queryKeys.subscriptions.detail(variables.subscriptionId) });
            notify.success('operation.success', 'operation.remove_payment.success');
        },
        onError: (error: any) => {
            notify.error('operation.error', 'operation.remove_payment.error');
        }
    });
};
export const useSharedWithMe = (options?: any) => {
    return useQuery<ApiResponse<SharedWithMeData>>({
        queryKey: ['shared-with-me'] as const,
        queryFn: () => api.sharedWithMe.get(),
        staleTime: 30 * 1000, // 30 seconds
        ...options,
    });
};

export const useSharedWithMeOne = (resourceType: string, resourceId: number | string, options?: any) => {
    return useQuery<ApiResponse<any> & { permission_level?: string }>({
        queryKey: ['shared-with-me', resourceType, String(resourceId)] as const,
        queryFn: () => api.sharedWithMe.getOne(resourceType, resourceId),
        staleTime: 30 * 1000,
        enabled: !!resourceType && !!resourceId,
        ...options,
    });
};

export const useSendTariffsEmail = () => {
    return useMutation({
        mutationFn: (id: string | number) => api.tenantBusinesses.sendTariffsEmail(id),
        onSuccess: () => {
            notify.success('operation.success', 'email_sent');
        },
        onError: () => {
            notify.error('operation.error', 'email_send_failed');
        }
    });
};

export const useSendBalanceEmail = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (data: { clientId: number | string; clientType: 'business' | 'person'; emails: string[]; startDay?: string; endDay?: string; tenantBusinessId?: number | string; paymentStatusFilter?: string | string[]; fromEmail?: string; customMessage?: string }) => {
            const { tenantBusinessId, ...payload } = data;
            return api.balance.sendEmail(payload);
        },
        onSuccess: (data, variables) => {
            notify.success('operation.success', 'email_sent');
            if (variables.tenantBusinessId) {
                queryClient.invalidateQueries({ queryKey: queryKeys.tenantBusinesses.emailLogs(variables.tenantBusinessId) });
            }
        },
        onError: () => {
            notify.error('operation.error', 'email_send_failed');
        }
    });
};

export const useSendReservationSettlementEmail = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (data: { id: number | string; emails: string[]; subject?: string; messageId?: string; fromEmail?: string; customMessage?: string }) =>
            api.reservations.sendTripReservationEmail(data.id, { emails: data.emails, subject: data.subject, messageId: data.messageId, fromEmail: data.fromEmail, customMessage: data.customMessage }),
        onSuccess: (data, variables) => {
            notify.success('operation.success', 'emails_sent_successfully');
            queryClient.invalidateQueries({ queryKey: queryKeys.reservations.emailLogs(variables.id) });
        },
        onError: () => {
            notify.error('operation.error', 'failed_to_send_emails');
        }
    });
};

export const useReservationEmailLogs = (id: string | number | undefined, options?: any) => {
    return useQuery<ApiResponse<any[]>, Error>({
        queryKey: queryKeys.reservations.emailLogs(id!),
        queryFn: () => api.reservations.getEmailLogs(id!),
        enabled: !!id,
        ...options,
    });
};

export const useGoogleAuthStatus = (options?: any) => {
    return useQuery({
        queryKey: queryKeys.googleIntegration.status(),
        queryFn: () => api.googleIntegration.getStatus(),
        ...options,
    });
};

export const useGoogleAccounts = (options?: any) => {
    return useQuery({
        queryKey: queryKeys.googleIntegration.accounts(),
        queryFn: () => api.googleIntegration.getAccounts(),
        ...options,
    });
};

export const useDeleteGoogleAccount = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (id: number) => api.googleIntegration.deleteAccount(id),
        onSuccess: () => {
            notify.success('operation.success', 'google_account_disconnected');
            queryClient.invalidateQueries({ queryKey: queryKeys.googleIntegration.all });
        },
        onError: () => {
            notify.error('operation.error', 'operation_error');
        }
    });
};

export const useGoogleAuthCallback = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (data: { code: string; redirectUri: string }) => api.googleIntegration.callback(data.code, data.redirectUri),
        onSuccess: () => {
            notify.success('operation.success', 'google_account_connected');
            queryClient.invalidateQueries({ queryKey: queryKeys.googleIntegration.all });
        },
        onError: () => {
            notify.error('operation.error', 'google_account_connection_failed');
        }
    });
};

export const useDisconnectGoogle = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: () => api.googleIntegration.disconnect(),
        onSuccess: () => {
            notify.success('operation.success', 'google_account_disconnected');
            queryClient.invalidateQueries({ queryKey: queryKeys.googleIntegration.all });
        },
        onError: () => {
            notify.error('operation.error', 'operation_error');
        }
    });
};

export const useSearchGmail = (query: string, options?: any) => {
    return useQuery({
        queryKey: queryKeys.googleIntegration.search(query),
        queryFn: () => api.googleIntegration.search(query),
        enabled: !!query && query.trim().length > 1,
        ...options,
    });
};

export const useReservationEmailThreads = (reservationId: string | number | undefined, options?: any) => {
    return useQuery<ApiResponse<any[]>, Error>({
        queryKey: queryKeys.reservations.emailThreads(reservationId!),
        queryFn: () => api.reservations.getEmailThreads(reservationId!),
        enabled: !!reservationId,
        ...options,
    });
};

export const useReservationEmailThreadDetails = (reservationId: string | number | undefined, threadId: string | undefined, options?: any) => {
    return useQuery<ApiResponse<any>, Error>({
        queryKey: queryKeys.reservations.emailThreadDetails(reservationId!, threadId!),
        queryFn: () => api.reservations.getEmailThreadDetails(reservationId!, threadId!),
        enabled: !!reservationId && !!threadId,
        ...options,
    });
};

export const useLinkReservationEmailThread = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (data: { reservationId: number | string; gmail_thread_id: string; google_account_id?: number; subject?: string; snippet?: string }) =>
            api.reservations.linkEmailThread(data.reservationId, {
                gmail_thread_id: data.gmail_thread_id,
                google_account_id: data.google_account_id,
                subject: data.subject,
                snippet: data.snippet
            }),
        onSuccess: (data, variables) => {
            notify.success('operation.success', 'email_thread_linked_successfully');
            queryClient.invalidateQueries({ queryKey: queryKeys.reservations.emailThreads(variables.reservationId) });
        },
        onError: () => {
            notify.error('operation.error', 'failed_to_link_email_thread');
        }
    });
};

export const useUnlinkReservationEmailThread = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (data: { reservationId: number | string; threadId: string }) =>
            api.reservations.unlinkEmailThread(data.reservationId, data.threadId),
        onSuccess: (data, variables) => {
            notify.success('operation.success', 'email_thread_unlinked_successfully');
            queryClient.invalidateQueries({ queryKey: queryKeys.reservations.emailThreads(variables.reservationId) });
        },
        onError: () => {
            notify.error('operation.error', 'operation_error');
        }
    });
};

export const useReplyReservationEmailThread = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (data: {
            reservationId: number | string;
            threadId: string;
            to: string;
            cc?: string;
            subject: string;
            bodyHtml: string;
            bodyText?: string;
            replyAll?: boolean;
            inReplyTo?: string;
            references?: string;
        }) => api.reservations.replyEmailThread(data.reservationId, data.threadId, data),
        onSuccess: (data, variables) => {
            notify.success('operation.success', 'email_reply_sent_successfully');
            queryClient.invalidateQueries({ queryKey: queryKeys.reservations.emailThreads(variables.reservationId) });
            queryClient.invalidateQueries({ queryKey: queryKeys.reservations.emailThreadDetails(variables.reservationId, variables.threadId) });
        },
        onError: () => {
            notify.error('operation.error', 'failed_to_send_reply');
        }
    });
};

export const useComposeReservationEmailThread = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (data: {
            reservationId: number | string;
            to: string;
            cc?: string;
            subject: string;
            bodyHtml: string;
        }) => api.reservations.composeEmailThread(data.reservationId, data),
        onSuccess: (data, variables) => {
            notify.success('operation.success', 'email_sent_successfully');
            queryClient.invalidateQueries({ queryKey: queryKeys.reservations.emailThreads(variables.reservationId) });
        },
        onError: () => {
            notify.error('operation.error', 'failed_to_send_email');
        }
    });
};

export const useFindReconciliationMatches = () => {
    return useMutation({
        mutationFn: (data: { seller_id: number; client_id: number; client_type: 'business' | 'person' }) => api.balance.findMatches(data),
        onError: () => {
            notify.error('operation.error', 'reconcile_find_error');
        }
    });
};

export const useExecuteReconciliation = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (data: { matches: { payment_id: number; reservation_id: number }[] }) => api.balance.executeReconciliation(data),
        onSuccess: () => {
            notify.success('operation.success', 'reconciliation_executed_successfully');
            queryClient.invalidateQueries({ queryKey: queryKeys.balance.all });
            queryClient.invalidateQueries({ queryKey: queryKeys.reservations.all });
            queryClient.invalidateQueries({ queryKey: queryKeys.services.all });
        },
        onError: () => {
            notify.error('operation.error', 'reconciliation_failed');
        }
    });
};

export const useSendBulkEmail = () => {
    return useMutation({
        mutationFn: (data: { businessIds?: (string | number)[] | 'all'; templateId?: string | number; manualEmails?: string[]; fromEmail?: string }) => api.tenantBusinesses.sendBulkEmail(data),
        onSuccess: () => {
            notify.success('operation.success', 'email_sent');
        },
        onError: () => {
            notify.error('operation.error', 'email_send_failed');
        }
    });
};

export const useSendBulkTariffsEmail = useSendBulkEmail;

export const useSendAssignmentsEmail = () => {
    return useMutation({
        mutationFn: (data: { businessId: string | number; date: string; manualEmails?: string[]; reservationsCount?: number; fromEmail?: string }) => api.tenantBusinesses.sendAssignmentsEmail(data),
        onSuccess: () => {
            notify.success('operation.success', 'email_sent');
        },
        onError: () => {
            notify.error('operation.error', 'email_send_failed');
        }
    });
};

export const useEmailTemplates = (params?: any, options?: any) => useQuery<ApiResponse<any[]>, Error>({
    queryKey: ['email-templates', params],
    queryFn: () => api.emailTemplates.getAll(params),
    ...options
});

export const useEmailTemplate = (id: string | number, options?: any) => useQuery<ApiResponse<any>, Error>({
    queryKey: ['email-templates', id],
    queryFn: () => api.emailTemplates.getOne(id),
    enabled: !!id,
    ...options
});

export const useCreateEmailTemplate = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (data: any) => api.emailTemplates.save(data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['email-templates'] });
            notify.success('operation.success', 'operation.save.success');
        },
        onError: () => notify.error('operation.error', 'operation.save.error')
    });
};

export const useUpdateEmailTemplate = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (data: any) => api.emailTemplates.save(data),
        onSuccess: (_, variables) => {
            queryClient.invalidateQueries({ queryKey: ['email-templates'] });
            queryClient.invalidateQueries({ queryKey: ['email-templates', variables.id] });
            notify.success('operation.success', 'operation.save.success');
        },
        onError: () => notify.error('operation.error', 'operation.save.error')
    });
};

export const useDeleteEmailTemplate = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (id: string | number) => api.emailTemplates.delete(id),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['email-templates'] });
            notify.success('operation.success', 'operation.delete.success');
        },
        onError: () => notify.error('operation.error', 'operation.delete.error')
    });
};

export const useSavedReports = (params?: any, options?: any) => useQuery<ApiResponse<SavedReport[]>, Error>({
    queryKey: ['saved-reports', params],
    queryFn: () => api.savedReports.getAll(params),
    ...options
});

export const useSavedReport = (id: string | number, options?: any) => useQuery<ApiResponse<SavedReport>, Error>({
    queryKey: ['saved-reports', id],
    queryFn: () => api.savedReports.getOne(id),
    enabled: !!id,
    ...options
});

export const useCreateSavedReport = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (data: Partial<SavedReport>) => api.savedReports.save(data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['saved-reports'] });
            notify.success('operation.smart.title', 'operation.save_saved_report.success');
        },
        onError: () => notify.error('operation.smart.title', 'operation.save_saved_report.error')
    });
};

export const useUpdateSavedReport = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (data: SavedReport) => api.savedReports.save(data),
        onSuccess: (_, variables) => {
            queryClient.invalidateQueries({ queryKey: ['saved-reports'] });
            queryClient.invalidateQueries({ queryKey: ['saved-reports', variables.id] });
            notify.success('operation.smart.title', 'operation.save_saved_report.success');
        },
        onError: () => notify.error('operation.smart.title', 'operation.save_saved_report.error')
    });
};

export const useDeleteSavedReport = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (id: string | number) => api.savedReports.delete(id),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['saved-reports'] });
            notify.success('operation.smart.title', 'operation.delete_saved_report.success');
        },
        onError: () => notify.error('operation.smart.title', 'operation.delete_saved_report.error')
    });
};

export const useCreatePayment = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (data: Partial<Payment>) => api.payments.save(data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: queryKeys.payments.all });
            notify.success('operation.success', 'operation.save_payment.success');
        },
        onError: (error: any) => {
            notify.error('operation.error', 'operation.save_payment.error');
        }
    });
};

export const useUpdatePayment = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (data: Partial<Payment>) => api.payments.save(data),
        onSuccess: (_, variables) => {
            queryClient.invalidateQueries({ queryKey: queryKeys.payments.all });
            queryClient.invalidateQueries({ queryKey: queryKeys.payments.detail(variables.id!) });
            notify.success('operation.success', 'operation.save_payment.success');
        },
        onError: (error: any) => {
            notify.error('operation.error', 'operation.save_payment.error');
        }
    });
};

export const useDeletePayment = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (id: number) => api.payments.delete(id),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: queryKeys.payments.all });
            notify.success('operation.success', 'operation.delete.success');
        },
        onError: (error: any) => {
            notify.error('operation.error', 'operation.delete.error');
        }
    });
};

export const useAutoAssignPaymentsPreview = () => {
    return useMutation({
        mutationFn: (params?: { business_id?: number; payment_id?: number }) => api.payments.autoAssignPreview(params)
    });
};

export const useAutoAssignPaymentsConfirm = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (params: { business_id?: number; payment_id?: number; assignments?: any[] }) =>
            api.payments.autoAssignConfirm(params),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: queryKeys.payments.all });
            queryClient.invalidateQueries({ queryKey: ['reservations'] });
            queryClient.invalidateQueries({ queryKey: ['service-bookings'] });
            queryClient.invalidateQueries({ queryKey: ['payments'] });
            queryClient.invalidateQueries({ queryKey: ['finances'] });
            queryClient.invalidateQueries({ queryKey: ['business-balance'] });
        }
    });
};

// Notification Hooks
export const useNotifications = (params?: any, options?: any) => useQuery<ApiResponse<any[]>, Error>({
    ...notificationQueries.all(params),
    ...options
});

export const useNotification = (id: string | number, options?: any) => useQuery<ApiResponse<any>, Error>({
    ...notificationQueries.detail(id),
    ...options
});

export const useMarkAllNotificationsSeen = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: () => api.notifications.markAllSeen(),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: queryKeys.notifications.all });
            notify.success("operation.success", "all_notifications_marked_seen");
        },
    });
};

export const useSaveNotification = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (data: any) => api.notifications.save(data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: queryKeys.notifications.all });
        },
    });
};
export const useDeleteServiceGroupCover = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (id: string | number) => api.serviceGroups.removeCover(id),
        onSuccess: (data, variables) => {
            queryClient.invalidateQueries({ queryKey: queryKeys.serviceGroups.detail(variables) });
            queryClient.invalidateQueries({ queryKey: queryKeys.serviceGroups.lists() });
        },
    });
};

export const activityQueries = {
    all: (params?: any) => queryOptions({
        queryKey: queryKeys.activities.list(params),
        queryFn: () => api.activities.getAll(params),
        staleTime: 30 * 1000,
    }),
};

export const useActivities = (params?: any, options?: any) => useQuery<ApiResponse<Activity[]>, Error>({
    ...activityQueries.all(params),
    ...options
});

export const trackedFlightQueries = {
    all: (params?: any) => queryOptions({
        queryKey: ['tracked-flights', 'list', params] as const,
        queryFn: () => api.trackedFlights.getAll(params),
        staleTime: 30 * 1000,
    }),
};

export const useTrackedFlights = (params?: any, options?: any) => useQuery<ApiResponse<TrackedFlight[]>, Error>({
    ...trackedFlightQueries.all(params),
    ...options
});

export const useSyncAllTrackedFlights = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: () => api.trackedFlights.syncAll(),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['tracked-flights'] });
        }
    });
};
